
"use client";

import { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  Skeleton,
  Paper,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { PeopleOutline, AssessmentOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiClient from "../context/apiClient";
import Layout from "../components/common/Layout";

const AdminDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  const [topCards, setTopCards] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    regional_performance: [],
    recent_system_activity: [],
    kpi_section: {},
  });

  const kpiConfig = [
    {
      key: "profile_completion_rate",
      label: "Profile Completion Rate",
      suffix: "%",
      color: theme.palette.info.main,
    },
    {
      key: "interview_success_rate",
      label: "Interview Success Rate",
      suffix: "%",
      color: theme.palette.success.main,
    },
    {
      key: "average_placement_time_days",
      label: "Average Placement Time",
      suffix: " Days",
      color: theme.palette.warning.main,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get("/dashboard/admin/");
        const apiData = response.data;

        const mappedKpiCards = apiData.kpi_cards
          ? [
              {
                label: "ACTIVE JOBS",
                value: apiData.kpi_cards.active_jobs || 0,
              },
              {
                label: "TOTAL APPLICATIONS",
                value: apiData.kpi_cards.total_applications || 0,
              },
              {
                label: "INTERVIEWS SCHEDULED",
                value: apiData.kpi_cards.interviews_scheduled || 0,
              },
              {
                label: "OFFERS EXTENDED",
                value: apiData.kpi_cards.offers_extended || 0,
              },
            ]
          : [];
        setTopCards(mappedKpiCards);

        const mappedActivity = (apiData.recent_system_activity || []).map(
          (activity) => ({
            type: activity.type,
            description: activity.description,
            timestamp: activity.time_ago,
          })
        );

        const mappedRegionalPerformance = (
          apiData.regional_performance || []
        ).map((region) => ({
          ...region,
          growth: region.growth,
          placements: region.placements,
        }));

        const mappedKpiSection = {
          ...(apiData.key_performance_indicators || {}),
          employer_satisfaction_rate: 93.0,
        };

        setDashboardData({
          recent_system_activity: mappedActivity,
          regional_performance: mappedRegionalPerformance,
          kpi_section: mappedKpiSection,
        });
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const quickActions = [
    {
      label: "View Analytics",
      link: "/db-shiksha/gatep/admin/analytics",
      icon: <AssessmentOutlined />,
    },
    {
      label: "Manage Users",
      link: "/db-shiksha/gatep/admin/users",
      icon: <PeopleOutline />,
    },
  ];

  const cardStyles = {
    borderRadius: "12px",
    boxShadow: theme.shadows,
    height: "100%",
    backgroundColor: theme.palette.mint?.main || theme.palette.background.paper,
    p: 2,
  };

  return (
    <Layout title="Admin Dashboard">
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Stack direction="column" spacing={3}>
          <Paper
            sx={{
              p: 3,
              backgroundColor:
                theme.palette.mint?.main || theme.palette.background.paper,
              borderRadius: "12px",
              boxShadow: theme.shadows,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                mb: 2,
              }}
            >
              Quick Action
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {quickActions.map((action, index) => (
                <Button
                  key={index}
                  variant={index === 0 ? "contained" : "outlined"}
                  color="primary"
                  startIcon={action.icon}
                  onClick={() => navigate(action.link)}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          </Paper>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: { xs: 1.5, sm: 2, md: 3 },
            }}
          >
            {isLoading
              ? [...Array(4)].map((_, index) => (
                  <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={140}
                      sx={{ borderRadius: "12px" }}
                    />
                  </Box>
                ))
              : topCards.map((card, index) => (
                  <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: 140,
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: "20px",
                          boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                          position: "relative",
                          overflow: "hidden",
                          p: { xs: 2, sm: 2.5, md: 3 },
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          transition: "transform 0.3s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-5px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 0,
                          }}
                        >
                          <svg
                            width="100%"
                            height="100%"
                            viewBox="0 0 280 140"
                            preserveAspectRatio="none"
                          >
                            <path
                              d="M210 0 C 240 40, 200 100, 240 140 L 280 140 L 280 0 Z"
                              fill={alpha(theme.palette.common.white, 0.15)}
                            />
                          </svg>
                        </Box>
                        <Box
                          sx={{
                            position: "relative",
                            zIndex: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 800,
                              color: alpha(theme.palette.primary.contrastText, 0.7),
                              textTransform: "uppercase",
                              lineHeight: 1,
                              letterSpacing: "0.02em",
                            }}
                          >
                            {card.label}
                          </Typography>
                        </Box>
                        <Box sx={{ position: "relative", zIndex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "3rem",
                              fontWeight: 900,
                              color: theme.palette.primary.contrastText,
                              lineHeight: 1,
                            }}
                          >
                            {card.value}
                          </Typography>
                        </Box>
                      </Box>
                  </Box>
                ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box sx={{ flex: { md: 5 }, minWidth: 0 }}>
              <Paper sx={cardStyles}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: theme.palette.primary.main, mb: 2 }}
                >
                  Regional Performance
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          "& .MuiTableCell-root": {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            color: theme.palette.primary.main,
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                            py: 1.5,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          },
                        }}
                      >
                        <TableCell>Region</TableCell>
                        <TableCell align="center">Placement</TableCell>
                        <TableCell align="center">Growth</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading
                        ? [...Array(6)].map((_, i) => (
                            <TableRow key={i}>
                              <TableCell colSpan={3}>
                                {" "}
                                <Skeleton />{" "}
                              </TableCell>
                            </TableRow>
                          ))
                        : dashboardData.regional_performance.map(
                            (region, i) => (
                              <TableRow
                                key={i}
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                  "&:hover": {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                  },
                                }}
                              >
                                <TableCell
                                  sx={{
                                    fontWeight: "500",
                                    color: theme.palette.text.primary,
                                    py: 1.5,
                                  }}
                                >
                                  {region.region}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "600",
                                    color: theme.palette.secondary.main,
                                    py: 1.5,
                                  }}
                                >
                                  {region.placements}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: "500",
                                    color: theme.palette.secondary.main,
                                    py: 1.5,
                                  }}
                                >
                                  {`${region.growth}%`}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
            <Box sx={{ flex: { md: 4 }, minWidth: 0 }}>
              <Paper sx={cardStyles}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: theme.palette.primary.main, mb: 2 }}
                >
                  Recent System Activity
                </Typography>
                <Stack spacing={2.5}>
                  {isLoading
                    ? [...Array(5)].map((_, i) => (
                        <Skeleton key={i} height={50} />
                      ))
                    : dashboardData.recent_system_activity.map(
                        (activity, i) => (
                          <Box key={i} sx={{ position: "relative", pl: 2 }}>
                            <Box
                              sx={{
                                position: "absolute",
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: "8px",
                                height: "8px",
                                // Changed from green to orange
                                backgroundColor: theme.palette.warning.main,
                                borderRadius: "50%",
                              }}
                            />
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              mb={0.5}
                            >
                              <Chip
                                label={activity.type}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(
                                    theme.palette.secondary.main,
                                    0.1
                                  ),
                                  color: theme.palette.secondary.main,
                                  borderRadius: "8px",
                                  fontSize: "0.75rem",
                                  fontWeight: "500",
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.error.main,
                                  fontSize: "0.75rem",
                                  fontWeight: "500",
                                }}
                              >
                                {activity.timestamp}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "500",
                                color: theme.palette.text.primary,
                                lineHeight: 1.4,
                              }}
                            >
                              {activity.description}
                            </Typography>
                          </Box>
                        )
                      )}
                </Stack>
              </Paper>
            </Box>
            <Box sx={{ flex: { md: 3 }, minWidth: 0 }}>
              <Paper sx={cardStyles}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: theme.palette.primary.main, mb: 2 }}
                >
                  {isLoading ? (
                    <Skeleton width="70%" />
                  ) : (
                    "Key Performance Indicators"
                  )}
                </Typography>
                {isLoading ? (
                  <Stack spacing={3} sx={{ textAlign: "center", mt: 2 }}>
                    {[...Array(3)].map((_, i) => (
                      <Box key={i}>
                        <Skeleton
                          variant="text"
                          width="60%"
                          height={40}
                          sx={{ mx: "auto" }}
                        />
                        <Skeleton
                          variant="text"
                          width="80%"
                          height={20}
                          sx={{ mx: "auto" }}
                        />
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Stack spacing={3} sx={{ textAlign: "center", mt: 1 }}>
                    {kpiConfig.map((kpi) => (
                      <Box key={kpi.key}>
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          sx={{
                            color: kpi.color,
                            fontSize: "2.5rem",
                            lineHeight: 1,
                            mb: 0.5,
                          }}
                        >
                          {Math.round(dashboardData.kpi_section[kpi.key] || 0)}
                          {kpi.suffix}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            lineHeight: 1.2,
                          }}
                        >
                          {kpi.label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Box>
          </Box>
        </Stack>
      </Box>
    </Layout>
  );
};

export default AdminDashboard;

// "use client";

// import { useState, useEffect } from "react";
// import {
//   Grid,
//   Typography,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Button,
//   Stack,
//   Skeleton,
//   Paper,
// } from "@mui/material";
// import { useTheme, alpha } from "@mui/material/styles";
// import { PeopleOutline, AssessmentOutlined } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";

// const AdminDashboard = () => {
//   const theme = useTheme();
//   const navigate = (path) => {
//     console.log(`Navigate to: ${path}`);
//   };

//   const [isLoading, setIsLoading] = useState(true);

//   const [topCards, setTopCards] = useState([]);
//   const [dashboardData, setDashboardData] = useState({
//     regional_performance: [],
//     recent_system_activity: [],
//     kpi_section: {},
//   });

//   useEffect(() => {
//     const fetchData = async () => {
//       setIsLoading(true);
//       try {
//         const response = await apiClient.get("/dashboard/admin/");
//         const apiData = response.data;

//         const mappedKpiCards = apiData.kpi_cards
//           ? [
//               {
//                 label: "ACTIVE JOBS",
//                 value: apiData.kpi_cards.active_jobs || 0,
//               },
//               {
//                 label: "TOTAL APPLICATIONS",
//                 value: apiData.kpi_cards.total_applications || 0,
//               },
//               {
//                 label: "INTERVIEWS SCHEDULED",
//                 value: apiData.kpi_cards.interviews_scheduled || 0,
//               },
//               {
//                 label: "OFFERS EXTENDED",
//                 value: apiData.kpi_cards.offers_extended || 0,
//               },
//             ]
//           : [];
//         setTopCards(mappedKpiCards);

//         const mappedActivity = (apiData.recent_system_activity || []).map(
//           (activity) => ({
//             type: activity.type,
//             description: activity.description,
//             timestamp: activity.time_ago,
//           })
//         );

//         const mappedRegionalPerformance = (
//           apiData.regional_performance || []
//         ).map((region) => ({
//           ...region,
//           growth: region.growth,
//           placements: region.placements,
//         }));

//         const mappedKpiSection = {
//           ...(apiData.key_performance_indicators || {}),
//           employer_satisfaction_rate: 93.0,
//         };

//         setDashboardData({
//           recent_system_activity: mappedActivity,
//           regional_performance: mappedRegionalPerformance,
//           kpi_section: mappedKpiSection,
//         });
//       } catch (error) {
//         console.error("Failed to fetch dashboard data:", error);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   const quickActions = [
//     {
//       label: "View Analytics",
//       link: "/gatep/admin/analytics",
//       icon: <AssessmentOutlined />,
//     },
//     {
//       label: "Manage Users",
//       link: "/gatep/admin/user",
//       icon: <PeopleOutline />,
//     },
//   ];

//   const kpiConfig = [
//     {
//       key: "profile_completion_rate",
//       label: "Profile Completion Rate",
//       suffix: "%",
//       color: theme.palette.info.main,
//     },
//     {
//       key: "interview_success_rate",
//       label: "Interview Success Rate",
//       suffix: "%",
//       color: theme.palette.success.main,
//     },
//     {
//       key: "average_placement_time_days",
//       label: "Average Placement Time",
//       suffix: " Days",
//       color: theme.palette.warning.main,
//     },
//   ];

//   const cardStyles = {
//     borderRadius: "12px",
//     boxShadow: theme.shadows,
//     height: "100%",
//     backgroundColor: theme.palette.mint.main,
//     p: 2,
//   };

//   return (
//     <Layout title="Admin Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           backgroundColor: theme.palette.background.default,
//           minHeight: "100vh",
//         }}
//       >
//         <Stack direction="column" spacing={3}>
//           <Paper
//             sx={{
//               p: 3,
//               backgroundColor: theme.palette.mint.main,
//               borderRadius: "12px",
//               boxShadow: theme.shadows,
//             }}
//           >
//             <Typography
//               variant="h4"
//               sx={{
//                 fontWeight: "bold",
//                 color: theme.palette.primary.main,
//                 mb: 2,
//               }}
//             >
//               Quick Action
//             </Typography>
//             <Stack direction="row" spacing={2} alignItems="center">
//               {quickActions.map((action, index) => (
//                 <Button
//                   key={index}
//                   variant={index === 0 ? "contained" : "outlined"}
//                   color="primary"
//                   startIcon={action.icon}
//                   onClick={() => navigate(action.link)}
//                   sx={{
//                     borderRadius: "12px",
//                     textTransform: "none",
//                     fontWeight: 500,
//                   }}
//                 >
//                   {action.label}
//                 </Button>
//               ))}
//             </Stack>
//           </Paper>

//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "row",
//               gap: { xs: 1.5, sm: 2, md: 3 },
//             }}
//           >
//             {isLoading
//               ? [...Array(4)].map((_, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Skeleton
//                       variant="rectangular"
//                       width="100%"
//                       height={140}
//                       sx={{ borderRadius: "12px" }}
//                     />
//                   </Box>
//                 ))
//               : topCards.map((card, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Box
//                       sx={{
//                         width: "100%",
//                         height: 140,
//                         backgroundColor: theme.palette.primary.main,
//                         borderRadius: "12px",
//                         boxShadow: theme.shadows,
//                         position: "relative",
//                         overflow: "hidden",
//                         p: { xs: 1.5, sm: 2, md: 2.5 },
//                         display: "flex",
//                         flexDirection: "column",
//                         justifyContent: "space-between",
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           position: "absolute",
//                           top: 0,
//                           right: 0,
//                           width: "100%",
//                           height: "100%",
//                           zIndex: 0,
//                         }}
//                       >
//                         <svg
//                           width="100%"
//                           height="100%"
//                           viewBox="0 0 280 140"
//                           preserveAspectRatio="none"
//                           style={{ position: "absolute", right: 0, top: 0 }}
//                         >
//                           <path
//                             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
//                             fill={theme.palette.accent.main}
//                           />
//                         </svg>
//                       </Box>
//                       <Box
//                         sx={{
//                           position: "relative",
//                           zIndex: 1,
//                         }}
//                       >
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "0.65rem", sm: "0.75rem" },
//                             fontWeight: 600,
//                             color: alpha(
//                               theme.palette.primary.contrastText,
//                               0.8
//                             ),
//                             textTransform: "uppercase",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {card.label}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ position: "relative", zIndex: 1 }}>
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "1.75rem", md: "2.25rem" },
//                             fontWeight: "bold",
//                             color: theme.palette.primary.contrastText,
//                             lineHeight: 1,
//                           }}
//                         >
//                           {card.value}
//                         </Typography>
//                       </Box>
//                     </Box>
//                   </Box>
//                 ))}
//           </Box>

//           <Box
//             sx={{
//               display: "flex",
//               gap: 3,
//               flexDirection: { xs: "column", md: "row" },
//             }}
//           >
//             <Box sx={{ flex: { md: 5 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: theme.palette.primary.main, mb: 2 }}
//                 >
//                   Regional Performance
//                 </Typography>
//                 <TableContainer>
//                   <Table size="small">
//                     <TableHead>
//                       <TableRow
//                         sx={{
//                           "& .MuiTableCell-root": {
//                             borderBottom: `1px solid ${theme.palette.divider}`,
//                             color: theme.palette.text.secondary,
//                             fontWeight: "600",
//                             fontSize: "0.875rem",
//                             py: 1,
//                           },
//                         }}
//                       >
//                         <TableCell>Region</TableCell>
//                         <TableCell align="center">Placement</TableCell>
//                         <TableCell align="center">Growth</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {isLoading
//                         ? [...Array(6)].map((_, i) => (
//                             <TableRow key={i}>
//                               <TableCell colSpan={3}>
//                                 {" "}
//                                 <Skeleton />{" "}
//                               </TableCell>
//                             </TableRow>
//                           ))
//                         : dashboardData.regional_performance.map(
//                             (region, i) => (
//                               <TableRow
//                                 key={i}
//                                 sx={{
//                                   "&:last-child td, &:last-child th": {
//                                     border: 0,
//                                   },
//                                   "&:hover": {
//                                     backgroundColor: theme.palette.action.hover,
//                                   },
//                                 }}
//                               >
//                                 <TableCell
//                                   sx={{
//                                     fontWeight: "500",
//                                     color: theme.palette.text.primary,
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {region.region}
//                                 </TableCell>
//                                 <TableCell
//                                   align="center"
//                                   sx={{
//                                     fontWeight: "600",
//                                     color: theme.palette.secondary.main,
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {region.placements}
//                                 </TableCell>
//                                 <TableCell
//                                   align="center"
//                                   sx={{
//                                     fontWeight: "500",
//                                     color: theme.palette.secondary.main,
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {`+${region.growth}%`}
//                                 </TableCell>
//                               </TableRow>
//                             )
//                           )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 4 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: theme.palette.primary.main, mb: 2 }}
//                 >
//                   Recent System Activity
//                 </Typography>
//                 <Stack spacing={2.5}>
//                   {isLoading
//                     ? [...Array(5)].map((_, i) => (
//                         <Skeleton key={i} height={50} />
//                       ))
//                     : dashboardData.recent_system_activity.map(
//                         (activity, i) => (
//                           <Box key={i} sx={{ position: "relative", pl: 2 }}>
//                             <Box
//                               sx={{
//                                 position: "absolute",
//                                 left: 0,
//                                 top: "50%",
//                                 transform: "translateY(-50%)",
//                                 width: "8px",
//                                 height: "8px",
//                                 backgroundColor: theme.palette.success.main,
//                                 borderRadius: "50%",
//                               }}
//                             />
//                             <Box
//                               display="flex"
//                               justifyContent="space-between"
//                               alignItems="flex-start"
//                               mb={0.5}
//                             >
//                               <Chip
//                                 label={activity.type}
//                                 size="small"
//                                 sx={{
//                                   backgroundColor: alpha(
//                                     theme.palette.secondary.main,
//                                     0.1
//                                   ),
//                                   color: theme.palette.secondary.main,
//                                   borderRadius: "8px",
//                                   fontSize: "0.75rem",
//                                   fontWeight: "500",
//                                 }}
//                               />
//                               <Typography
//                                 variant="caption"
//                                 sx={{
//                                   color: theme.palette.error.main,
//                                   fontSize: "0.75rem",
//                                   fontWeight: "500",
//                                 }}
//                               >
//                                 {activity.timestamp}
//                               </Typography>
//                             </Box>
//                             <Typography
//                               variant="body2"
//                               sx={{
//                                 fontWeight: "500",
//                                 color: theme.palette.text.primary,
//                                 lineHeight: 1.4,
//                               }}
//                             >
//                               {activity.description}
//                             </Typography>
//                           </Box>
//                         )
//                       )}
//                 </Stack>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 3 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: theme.palette.primary.main, mb: 2 }}
//                 >
//                   {isLoading ? (
//                     <Skeleton width="70%" />
//                   ) : (
//                     "Key Performance Indicators"
//                   )}
//                 </Typography>
//                 {isLoading ? (
//                   <Stack spacing={3} sx={{ textAlign: "center", mt: 2 }}>
//                     {[...Array(3)].map((_, i) => (
//                       <Box key={i}>
//                         <Skeleton
//                           variant="text"
//                           width="60%"
//                           height={40}
//                           sx={{ mx: "auto" }}
//                         />
//                         <Skeleton
//                           variant="text"
//                           width="80%"
//                           height={20}
//                           sx={{ mx: "auto" }}
//                         />
//                       </Box>
//                     ))}
//                   </Stack>
//                 ) : (
//                   <Stack spacing={3} sx={{ textAlign: "center", mt: 1 }}>
//                     {kpiConfig.map((kpi) => (
//                       <Box key={kpi.key}>
//                         <Typography
//                           variant="h3"
//                           fontWeight="bold"
//                           sx={{
//                             color: kpi.color,
//                             fontSize: "2.5rem",
//                             lineHeight: 1,
//                             mb: 0.5,
//                           }}
//                         >
//                           {Math.round(dashboardData.kpi_section[kpi.key] || 0)}
//                           {kpi.suffix}
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           sx={{
//                             color: theme.palette.text.secondary,
//                             fontSize: "0.875rem",
//                             fontWeight: "500",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {kpi.label}
//                         </Typography>
//                       </Box>
//                     ))}
//                   </Stack>
//                 )}
//               </Paper>
//             </Box>
//           </Box>
//         </Stack>
//       </Box>
//     </Layout>
//   );
// };

// export default AdminDashboard;

// "use client";

// import { useState, useEffect } from "react";
// import {
//   Grid,
//   Typography,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Button,
//   Stack,
//   Skeleton,
//   Paper,
// } from "@mui/material";
// import { useTheme, alpha } from "@mui/material/styles";
// import { PeopleOutline, AssessmentOutlined } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";

// const AdminDashboard = () => {
//   // `useTheme` hook is called here, providing the theme object
//   const theme = useTheme(); 
//   const navigate = (path) => {
//     console.log(`Navigate to: ${path}`);
//   };

//   const [isLoading, setIsLoading] = useState(true);

//   const [topCards, setTopCards] = useState([]);
//   const [dashboardData, setDashboardData] = useState({
//     regional_performance: [],
//     recent_system_activity: [],
//     kpi_section: {},
//   });

//   useEffect(() => {
//     const fetchData = async () => {
//       setIsLoading(true);
//       try {
//         const response = await apiClient.get("/dashboard/admin/");
//         const apiData = response.data;

//         const mappedKpiCards = apiData.kpi_cards
//           ? [
//               {
//                 label: "ACTIVE JOBS",
//                 value: apiData.kpi_cards.active_jobs || 0,
//               },
//               {
//                 label: "TOTAL APPLICATIONS",
//                 value: apiData.kpi_cards.total_applications || 0,
//               },
//               {
//                 label: "INTERVIEWS SCHEDULED",
//                 value: apiData.kpi_cards.interviews_scheduled || 0,
//               },
//               {
//                 label: "OFFERS EXTENDED",
//                 value: apiData.kpi_cards.offers_extended || 0,
//               },
//             ]
//           : [];
//         setTopCards(mappedKpiCards);

//         const mappedActivity = (apiData.recent_system_activity || []).map(
//           (activity) => ({
//             type: activity.type,
//             description: activity.description,
//             timestamp: activity.time_ago,
//           })
//         );

//         const mappedRegionalPerformance = (
//           apiData.regional_performance || []
//         ).map((region) => ({
//           ...region,
//           growth: region.growth,
//           placements: region.placements,
//         }));

//         const mappedKpiSection = {
//           ...(apiData.key_performance_indicators || {}),
//           employer_satisfaction_rate: 93.0,
//         };

//         setDashboardData({
//           recent_system_activity: mappedActivity,
//           regional_performance: mappedRegionalPerformance,
//           kpi_section: mappedKpiSection,
//         });
//       } catch (error) {
//         console.error("Failed to fetch dashboard data:", error);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   const quickActions = [
//     {
//       label: "View Analytics",
//       link: "/gatep/admin/analytics",
//       icon: <AssessmentOutlined />,
//     },
//     {
//       label: "Manage Users",
//       link: "/gatep/admin/user",
//       icon: <PeopleOutline />,
//     },
//   ];

//   // CORRECT: `kpiConfig` is defined INSIDE the component, after `useTheme()`.
//   // It now has access to the theme object.
//   const kpiConfig = [
//     {
//       key: "profile_completion_rate",
//       label: "Profile Completion Rate",
//       suffix: "%",
//       color: theme.palette.info.main,
//     },
//     {
//       key: "interview_success_rate",
//       label: "Interview Success Rate",
//       suffix: "%",
//       color: theme.palette.success.main,
//     },
//     {
//       key: "average_placement_time_days",
//       label: "Average Placement Time",
//       suffix: " Days",
//       color: theme.palette.warning.main,
//     },
//   ];

//   const cardStyles = {
//     borderRadius: "12px",
//     boxShadow: theme.shadows[1], 
//     height: "100%",
//     backgroundColor: theme.palette.mint.main, 
//     p: 2,
//   };

//   return (
//     <Layout title="Admin Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           backgroundColor: theme.palette.background.default, 
//           minHeight: "100vh",
//         }}
//       >
//         {/* ... The rest of your JSX code remains the same ... */}
//         <Stack direction="column" spacing={3}>
//           {/* ROW 1: QUICK ACTIONS */}
//           <Paper
//             sx={{
//               p: 3,
//               backgroundColor: theme.palette.mint.main, // Using theme color
//               borderRadius: "12px",
//               boxShadow: theme.shadows[1], // Using theme shadow
//             }}
//           >
//             <Typography
//               variant="h4"
//               sx={{
//                 fontWeight: "bold",
//                 color: theme.palette.primary.main,
//                 mb: 2,
//               }} // Using theme color
//             >
//               Quick Action
//             </Typography>
//             <Stack direction="row" spacing={2} alignItems="center">
//               {quickActions.map((action, index) => (
//                 <Button
//                   key={index}
//                   variant={index === 0 ? "contained" : "outlined"}
//                   color="primary" // Let the theme handle the colors
//                   startIcon={action.icon}
//                   onClick={() => navigate(action.link)}
//                   sx={{
//                     borderRadius: "12px",
//                     textTransform: "none",
//                     fontWeight: 500,
//                   }}
//                 >
//                   {action.label}
//                 </Button>
//               ))}
//             </Stack>
//           </Paper>

//           {/* ROW 2: CARDS (Always in one row, fluidly resizing) */}
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "row",
//               gap: { xs: 1.5, sm: 2, md: 3 },
//             }}
//           >
//             {isLoading
//               ? [...Array(4)].map((_, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Skeleton
//                       variant="rectangular"
//                       width="100%"
//                       height={140}
//                       sx={{ borderRadius: "12px" }}
//                     />
//                   </Box>
//                 ))
//               : topCards.map((card, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Box
//                       sx={{
//                         width: "100%",
//                         height: 140,
//                         backgroundColor: theme.palette.primary.main, // Using theme color
//                         borderRadius: "12px",
//                         boxShadow: theme.shadows[2], // Using theme shadow
//                         position: "relative",
//                         overflow: "hidden",
//                         p: { xs: 1.5, sm: 2, md: 2.5 },
//                         display: "flex",
//                         flexDirection: "column",
//                         justifyContent: "space-between",
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           position: "absolute",
//                           top: 0,
//                           right: 0,
//                           width: "100%",
//                           height: "100%",
//                           zIndex: 0,
//                         }}
//                       >
//                         <svg
//                           width="100%"
//                           height="100%"
//                           viewBox="0 0 280 140"
//                           preserveAspectRatio="none"
//                           style={{ position: "absolute", right: 0, top: 0 }}
//                         >
//                           <path
//                             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
//                             fill={theme.palette.accent.main} // Using theme accent color
//                           />
//                         </svg>
//                       </Box>
//                       <Box
//                         sx={{
//                           position: "relative",
//                           zIndex: 1,
//                         }}
//                       >
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "0.65rem", sm: "0.75rem" },
//                             fontWeight: 600,
//                             color: alpha(
//                               theme.palette.primary.contrastText,
//                               0.8
//                             ), // Using theme color
//                             textTransform: "uppercase",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {card.label}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ position: "relative", zIndex: 1 }}>
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "1.75rem", md: "2.25rem" },
//                             fontWeight: "bold",
//                             color: theme.palette.primary.contrastText, // Using theme color
//                             lineHeight: 1,
//                           }}
//                         >
//                           {card.value}
//                         </Typography>
//                       </Box>
//                     </Box>
//                   </Box>
//                 ))}
//           </Box>

//           {/* ROW 3: PANELS (Stack on mobile, resize proportionally on larger screens) */}
//           <Box
//             sx={{
//               display: "flex",
//               gap: 3,
//               flexDirection: { xs: "column", md: "row" },
//             }}
//           >
//             <Box sx={{ flex: { md: 5 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: theme.palette.primary.main, mb: 2 }} // Using theme color
//                 >
//                   Regional Performance
//                 </Typography>
//                 <TableContainer>
//                   <Table size="small">
//                     <TableHead>
//                       <TableRow
//                         sx={{
//                           "& .MuiTableCell-root": {
//                             borderBottom: `1px solid ${theme.palette.divider}`, // Using theme color
//                             color: theme.palette.text.secondary, // Using theme color
//                             fontWeight: "600",
//                             fontSize: "0.875rem",
//                             py: 1,
//                           },
//                         }}
//                       >
//                         <TableCell>Region</TableCell>
//                         <TableCell align="center">Placement</TableCell>
//                         <TableCell align="center">Growth</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {isLoading
//                         ? [...Array(6)].map((_, i) => (
//                             <TableRow key={i}>
//                               <TableCell colSpan={3}>
//                                 {" "}
//                                 <Skeleton />{" "}
//                               </TableCell>
//                             </TableRow>
//                           ))
//                         : dashboardData.regional_performance.map(
//                             (region, i) => (
//                               <TableRow
//                                 key={i}
//                                 sx={{
//                                   "&:last-child td, &:last-child th": {
//                                     border: 0,
//                                   },
//                                   "&:hover": {
//                                     backgroundColor: theme.palette.action.hover,
//                                   },
//                                 }}
//                               >
//                                 <TableCell
//                                   sx={{
//                                     fontWeight: "500",
//                                     color: theme.palette.text.primary,
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {region.region}
//                                 </TableCell>
//                                 <TableCell
//                                   align="center"
//                                   sx={{
//                                     fontWeight: "600",
//                                     color: theme.palette.secondary.main,
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {region.placements}
//                                 </TableCell>
//                                 <TableCell
//                                   align="center"
//                                   sx={{
//                                     fontWeight: "500",
//                                     color: theme.palette.secondary.main,
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {`+${region.growth}%`}
//                                 </TableCell>
//                               </TableRow>
//                             )
//                           )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 4 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: theme.palette.primary.main, mb: 2 }} // Using theme color
//                 >
//                   Recent System Activity
//                 </Typography>
//                 <Stack spacing={2.5}>
//                   {isLoading
//                     ? [...Array(5)].map((_, i) => (
//                         <Skeleton key={i} height={50} />
//                       ))
//                     : dashboardData.recent_system_activity.map(
//                         (activity, i) => (
//                           <Box key={i} sx={{ position: "relative", pl: 2 }}>
//                             <Box
//                               sx={{
//                                 position: "absolute",
//                                 left: 0,
//                                 top: "50%",
//                                 transform: "translateY(-50%)",
//                                 width: "8px",
//                                 height: "8px",
//                                 backgroundColor: theme.palette.success.main,
//                                 borderRadius: "50%",
//                               }}
//                             />
//                             <Box
//                               display="flex"
//                               justifyContent="space-between"
//                               alignItems="flex-start"
//                               mb={0.5}
//                             >
//                               <Chip
//                                 label={activity.type}
//                                 size="small"
//                                 sx={{
//                                   backgroundColor: alpha(
//                                     theme.palette.secondary.main,
//                                     0.1
//                                   ),
//                                   color: theme.palette.secondary.main,
//                                   borderRadius: "8px",
//                                   fontSize: "0.75rem",
//                                   fontWeight: "500",
//                                 }}
//                               />
//                               <Typography
//                                 variant="caption"
//                                 sx={{
//                                   color: theme.palette.error.main,
//                                   fontSize: "0.75rem",
//                                   fontWeight: "500",
//                                 }}
//                               >
//                                 {activity.timestamp}
//                               </Typography>
//                             </Box>
//                             <Typography
//                               variant="body2"
//                               sx={{
//                                 fontWeight: "500",
//                                 color: theme.palette.text.primary,
//                                 lineHeight: 1.4,
//                               }}
//                             >
//                               {activity.description}
//                             </Typography>
//                           </Box>
//                         )
//                       )}
//                 </Stack>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 3 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: theme.palette.primary.main, mb: 2 }} // Using theme color
//                 >
//                   {isLoading ? (
//                     <Skeleton width="70%" />
//                   ) : (
//                     "Key Performance Indicators"
//                   )}
//                 </Typography>
//                 {isLoading ? (
//                   <Stack spacing={3} sx={{ textAlign: "center", mt: 2 }}>
//                     {[...Array(3)].map((_, i) => (
//                       <Box key={i}>
//                         <Skeleton
//                           variant="text"
//                           width="60%"
//                           height={40}
//                           sx={{ mx: "auto" }}
//                         />
//                         <Skeleton
//                           variant="text"
//                           width="80%"
//                           height={20}
//                           sx={{ mx: "auto" }}
//                         />
//                       </Box>
//                     ))}
//                   </Stack>
//                 ) : (
//                   <Stack spacing={3} sx={{ textAlign: "center", mt: 1 }}>
//                     {kpiConfig.map((kpi) => (
//                       <Box key={kpi.key}>
//                         <Typography
//                           variant="h3"
//                           fontWeight="bold"
//                           sx={{
//                             color: kpi.color, // Using theme color from the config object
//                             fontSize: "2.5rem",
//                             lineHeight: 1,
//                             mb: 0.5,
//                           }}
//                         >
//                           {Math.round(dashboardData.kpi_section[kpi.key] || 0)}
//                           {kpi.suffix}
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           sx={{
//                             color: theme.palette.text.secondary,
//                             fontSize: "0.875rem",
//                             fontWeight: "500",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {kpi.label}
//                         </Typography>
//                       </Box>
//                     ))}
//                   </Stack>
//                 )}
//               </Paper>
//             </Box>
//           </Box>
//         </Stack>
//       </Box>
//     </Layout>
//   );
// };

// export default AdminDashboard;

// // // // "use client";

// // // // import { useState, useEffect } from "react";
// // // // import {
// // // //   Grid,
// // // //   Typography,
// // // //   Box,
// // // //   Chip,
// // // //   Table,
// // // //   TableBody,
// // // //   TableCell,
// // // //   TableContainer,
// // // //   TableHead,
// // // //   TableRow,
// // // //   Button,
// // // //   Stack,
// // // //   Skeleton,
// // // //   Paper,
// // // // } from "@mui/material";
// // // // import { PeopleOutline, AssessmentOutlined } from "@mui/icons-material";
// // // // import apiClient from "../context/apiClient";
// // // // import Layout from "../components/common/Layout";

// // // // const AdminDashboard = () => {
// // // //   const navigate = (path) => {
// // // //     console.log(`Navigate to: ${path}`);
// // // //   };

// // // //   const [isLoading, setIsLoading] = useState(true);

// // // //   const [topCards, setTopCards] = useState([]);
// // // //   const [dashboardData, setDashboardData] = useState({
// // // //     regional_performance: [],
// // // //     recent_system_activity: [],
// // // //     kpi_section: {},
// // // //   });

// // // //   useEffect(() => {
// // // //     const fetchData = async () => {
// // // //       setIsLoading(true);
// // // //       try {
// // // //         const response = await apiClient.get("/dashboard/admin/");
// // // //         const apiData = response.data;

// // // //         const mappedKpiCards = apiData.kpi_cards
// // // //           ? [
// // // //               {
// // // //                 label: "ACTIVE JOBS",
// // // //                 value: apiData.kpi_cards.active_jobs || 0,
// // // //               },
// // // //               {
// // // //                 label: "TOTAL APPLICATIONS",
// // // //                 value: apiData.kpi_cards.total_applications || 0,
// // // //               },
// // // //               {
// // // //                 label: "INTERVIEWS SCHEDULED",
// // // //                 value: apiData.kpi_cards.interviews_scheduled || 0,
// // // //               },
// // // //               {
// // // //                 label: "OFFERS EXTENDED",
// // // //                 value: apiData.kpi_cards.offers_extended || 0,
// // // //               },
// // // //             ]
// // // //           : [];
// // // //         setTopCards(mappedKpiCards);

// // // //         const mappedActivity = (apiData.recent_system_activity || []).map(
// // // //           (activity) => ({
// // // //             type: activity.type,
// // // //             description: activity.description,
// // // //             timestamp: activity.time_ago,
// // // //           })
// // // //         );

// // // //         const mappedRegionalPerformance = (
// // // //           apiData.regional_performance || []
// // // //         ).map((region) => ({
// // // //           ...region,
// // // //           growth: region.growth,
// // // //           placements: region.placements,
// // // //         }));

// // // //         const mappedKpiSection = {
// // // //           ...(apiData.key_performance_indicators || {}),
// // // //           employer_satisfaction_rate: 93.0,
// // // //         };

// // // //         setDashboardData({
// // // //           recent_system_activity: mappedActivity,
// // // //           regional_performance: mappedRegionalPerformance,
// // // //           kpi_section: mappedKpiSection,
// // // //         });
// // // //       } catch (error) {
// // // //         console.error("Failed to fetch dashboard data:", error);
// // // //       } finally {
// // // //         setIsLoading(false);
// // // //       }
// // // //     };

// // // //     fetchData();
// // // //   }, []);

// // // //   const quickActions = [
// // // //     {
// // // //       label: "View Analytics",
// // // //       link: "/gatep/admin/analytics",
// // // //       icon: <AssessmentOutlined />,
// // // //     },
// // // //     {
// // // //       label: "Manage Users",
// // // //       link: "/gatep/admin/user",
// // // //       icon: <PeopleOutline />,
// // // //     },
// // // //   ];

// // // //   const kpiConfig = [
// // // //     {
// // // //       key: "profile_completion_rate",
// // // //       label: "Profile Completion Rate",
// // // //       suffix: "%",
// // // //       color: "#42a5f5",
// // // //     },
// // // //     {
// // // //       key: "interview_success_rate",
// // // //       label: "Interview Success Rate",
// // // //       suffix: "%",
// // // //       color: "#66bb6a",
// // // //     },
// // // //     {
// // // //       key: "average_placement_time_days",
// // // //       label: "Average Placement Time",
// // // //       suffix: " Days",
// // // //       color: "#ffa726",
// // // //     },
// // // //   ];

// // // //   const cardStyles = {
// // // //     borderRadius: "12px",
// // // //     boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
// // // //     height: "100%",
// // // //     backgroundColor: "#F1FFFA",
// // // //     p: 2,
// // // //   };

// // // //   const primaryBlue = "#4C3CE2";

// // // //   return (
// // // //     <Layout>
// // // //       <Box
// // // //         sx={{
// // // //           p: { xs: 2, md: 3 },
// // // //           backgroundColor: "#f8f9fa",
// // // //           minHeight: "100vh",
// // // //         }}
// // // //       >
// // // //         <Grid container spacing={3}>
// // // //           <Grid item xs={12}>
// // // //             <Paper
// // // //               sx={{
// // // //                 p: 3,
// // // //                 mb: 3,
// // // //                 backgroundColor: "#F1FFFA",
// // // //                 borderRadius: "12px",
// // // //                 boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
// // // //               }}
// // // //             >
// // // //               <Typography
// // // //                 variant="h4"
// // // //                 sx={{ fontWeight: "bold", color: "#4F46BA" }}
// // // //               >
// // // //                 Quick Action
// // // //               </Typography>
// // // //               <Stack direction="row" spacing={2} alignItems="center">
// // // //                 {quickActions.map((action, index) => (
// // // //                   <Button
// // // //                     key={index}
// // // //                     variant={index === 0 ? "contained" : "outlined"}
// // // //                     startIcon={action.icon}
// // // //                     onClick={() => navigate(action.link)}
// // // //                     sx={{
// // // //                       borderRadius: "12px",
// // // //                       textTransform: "none",
// // // //                       fontWeight: 500,
// // // //                       boxShadow: "none",
// // // //                       backgroundColor:
// // // //                         index === 0 ? primaryBlue : "transparent",
// // // //                       borderColor: primaryBlue,
// // // //                       color: index === 0 ? "#FFFFFF" : primaryBlue,
// // // //                       "&:hover": {
// // // //                         backgroundColor:
// // // //                           index === 0 ? "#4032bf" : "rgba(76, 58, 226, 0.04)",
// // // //                         boxShadow: "none",
// // // //                       },
// // // //                     }}
// // // //                   >
// // // //                     {action.label}
// // // //                   </Button>
// // // //                 ))}
// // // //               </Stack>
// // // //             </Paper>

// // // //             <Grid container spacing={2} sx={{ marginTop: 3 }}>
// // // //               {isLoading
// // // //                 ? [...Array(4)].map((_, index) => (
// // // //                     <Grid item xs={12} sm={6} md={3} key={index}>
// // // //                       <Skeleton
// // // //                         variant="rectangular"
// // // //                         width="100%"
// // // //                         height={140}
// // // //                         sx={{ borderRadius: "12px" }}
// // // //                       />
// // // //                     </Grid>
// // // //                   ))
// // // //                 : topCards.map((card, index) => (
// // // //                     <Grid item xs={12} sm={6} md={3} key={index}>
// // // //                       <Box
// // // //                         sx={{
// // // //                           width: "280px",
// // // //                           height: "140px",
// // // //                           backgroundColor: "#4D3CC3",
// // // //                           borderRadius: "12px",
// // // //                           boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.15)",
// // // //                           position: "relative",
// // // //                           overflow: "hidden",
// // // //                           padding: "20px",
// // // //                           display: "flex",
// // // //                           flexDirection: "column",
// // // //                           justifyContent: "space-between",
// // // //                           margin: "0 auto",
// // // //                         }}
// // // //                       >
// // // //                         {/* Wave Vector Background */}
// // // //                         <Box
// // // //                           sx={{
// // // //                             position: "absolute",
// // // //                             top: 0,
// // // //                             right: 0,
// // // //                             width: "100%",
// // // //                             height: "100%",
// // // //                             zIndex: 0,
// // // //                           }}
// // // //                         >
// // // //                           <svg
// // // //                             width="100%"
// // // //                             height="100%"
// // // //                             viewBox="0 0 280 140"
// // // //                             preserveAspectRatio="none"
// // // //                             style={{ position: "absolute", right: 0, top: 0 }}
// // // //                           >
// // // //                             <path
// // // //                               d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
// // // //                               fill="#F9B64C"
// // // //                             />
// // // //                           </svg>
// // // //                         </Box>

// // // //                         {/* Content */}
// // // //                         <Box sx={{ position: "relative", zIndex: 1 }}>
// // // //                           <Typography
// // // //                             sx={{
// // // //                               fontSize: "12px",
// // // //                               fontWeight: 600,
// // // //                               color: "#B4B1D2",
// // // //                               textTransform: "uppercase",
// // // //                               lineHeight: 1.2,
// // // //                               letterSpacing: "0.5px",
// // // //                             }}
// // // //                           >
// // // //                             {card.label}
// // // //                           </Typography>
// // // //                         </Box>

// // // //                         <Box sx={{ position: "relative", zIndex: 1 }}>
// // // //                           <Typography
// // // //                             sx={{
// // // //                               fontSize: "32px",
// // // //                               fontWeight: "bold",
// // // //                               color: "#FFFFFF",
// // // //                               lineHeight: 1,
// // // //                             }}
// // // //                           >
// // // //                             {card.value}
// // // //                           </Typography>
// // // //                         </Box>
// // // //                       </Box>
// // // //                     </Grid>
// // // //                   ))}
// // // //             </Grid>
// // // //           </Grid>

// // // //           {/* Regional Performance - Left side, wider card */}
// // // //           <Grid item xs={12} md={5}>
// // // //             <Paper sx={cardStyles}>
// // // //               <Typography
// // // //                 variant="h6"
// // // //                 fontWeight="bold"
// // // //                 gutterBottom
// // // //                 sx={{ color: "#4D3CC3", mb: 2 }}
// // // //               >
// // // //                 Regional Performance
// // // //               </Typography>
// // // //               <TableContainer>
// // // //                 <Table size="small">
// // // //                   <TableHead>
// // // //                     <TableRow
// // // //                       sx={{
// // // //                         "& .MuiTableCell-root": {
// // // //                           borderBottom: "1px solid #E8F5E8",
// // // //                           color: "#666",
// // // //                           fontWeight: "600",
// // // //                           fontSize: "0.875rem",
// // // //                           py: 1,
// // // //                         },
// // // //                       }}
// // // //                     >
// // // //                       <TableCell>Region</TableCell>
// // // //                       <TableCell align="center">Placement</TableCell>
// // // //                       <TableCell align="center">Growth</TableCell>
// // // //                     </TableRow>
// // // //                   </TableHead>
// // // //                   <TableBody>
// // // //                     {isLoading
// // // //                       ? [...Array(6)].map((_, i) => (
// // // //                           <TableRow key={i}>
// // // //                             <TableCell colSpan={4}>
// // // //                               <Skeleton />
// // // //                             </TableCell>
// // // //                           </TableRow>
// // // //                         ))
// // // //                       : dashboardData.regional_performance.map((region, i) => (
// // // //                           <TableRow
// // // //                             key={i}
// // // //                             sx={{
// // // //                               "&:last-child td, &:last-child th": { border: 0 },
// // // //                               "&:hover": { backgroundColor: "#F8FFF8" },
// // // //                             }}
// // // //                           >
// // // //                             <TableCell
// // // //                               sx={{ fontWeight: "500", color: "#333", py: 1.5 }}
// // // //                             >
// // // //                               {region.region}
// // // //                             </TableCell>
// // // //                             <TableCell
// // // //                               align="center"
// // // //                               sx={{
// // // //                                 fontWeight: "600",
// // // //                                 color: "#2E7D32",
// // // //                                 py: 1.5,
// // // //                               }}
// // // //                             >
// // // //                               {region.placements}
// // // //                             </TableCell>
// // // //                             <TableCell
// // // //                               align="center"
// // // //                               sx={{
// // // //                                 fontWeight: "500",
// // // //                                 color: "#4CAF50",
// // // //                                 py: 1.5,
// // // //                               }}
// // // //                             >{`+${region.growth}%`}</TableCell>
// // // //                             {/* <TableCell align="center" sx={{ py: 1.5 }}>
// // // //                               <Chip
// // // //                                 label="Active"
// // // //                                 size="small"
// // // //                                 sx={{
// // // //                                   backgroundColor: "#4CAF50",
// // // //                                   color: "white",
// // // //                                   borderRadius: "12px",
// // // //                                   fontSize: "0.75rem",
// // // //                                   height: "24px",
// // // //                                 }}
// // // //                               />
// // // //                             </TableCell> */}
// // // //                           </TableRow>
// // // //                         ))}
// // // //                   </TableBody>
// // // //                 </Table>
// // // //               </TableContainer>
// // // //             </Paper>
// // // //           </Grid>

// // // //           {/* Recent System Activity - Center card */}
// // // //           <Grid item xs={12} md={4}>
// // // //             <Paper sx={cardStyles}>
// // // //               <Typography
// // // //                 variant="h6"
// // // //                 fontWeight="bold"
// // // //                 gutterBottom
// // // //                 sx={{ color: "#4D3CC3", mb: 2 }}
// // // //               >
// // // //                 Recent System Activity
// // // //               </Typography>
// // // //               <Stack spacing={2.5}>
// // // //                 {isLoading
// // // //                   ? [...Array(5)].map((_, i) => (
// // // //                       <Skeleton key={i} height={50} />
// // // //                     ))
// // // //                   : dashboardData.recent_system_activity.map((activity, i) => (
// // // //                       <Box key={i} sx={{ position: "relative", pl: 2 }}>
// // // //                         <Box
// // // //                           sx={{
// // // //                             position: "absolute",
// // // //                             left: 0,
// // // //                             top: "50%",
// // // //                             transform: "translateY(-50%)",
// // // //                             width: "8px",
// // // //                             height: "8px",
// // // //                             backgroundColor: "#4CAF50",
// // // //                             borderRadius: "50%",
// // // //                           }}
// // // //                         />
// // // //                         <Box
// // // //                           display="flex"
// // // //                           justifyContent="space-between"
// // // //                           alignItems="flex-start"
// // // //                           mb={0.5}
// // // //                         >
// // // //                           <Chip
// // // //                             label={activity.type}
// // // //                             size="small"
// // // //                             sx={{
// // // //                               backgroundColor: "#E8F5E8",
// // // //                               color: "#2E7D32",
// // // //                               borderRadius: "8px",
// // // //                               fontSize: "0.75rem",
// // // //                               fontWeight: "500",
// // // //                             }}
// // // //                           />
// // // //                           <Typography
// // // //                             variant="caption"
// // // //                             sx={{
// // // //                               color: "#FF5722",
// // // //                               fontSize: "0.75rem",
// // // //                               fontWeight: "500",
// // // //                             }}
// // // //                           >
// // // //                             {activity.timestamp}
// // // //                           </Typography>
// // // //                         </Box>
// // // //                         <Typography
// // // //                           variant="body2"
// // // //                           sx={{
// // // //                             fontWeight: "500",
// // // //                             color: "#333",
// // // //                             lineHeight: 1.4,
// // // //                           }}
// // // //                         >
// // // //                           {activity.description}
// // // //                         </Typography>
// // // //                       </Box>
// // // //                     ))}
// // // //               </Stack>
// // // //             </Paper>
// // // //           </Grid>

// // // //           {/* Key Performance Indicators - Right side, slim card */}
// // // //           <Grid item xs={12} md={3} sx={{ width: "20%" }}>
// // // //             <Paper sx={cardStyles}>
// // // //               <Typography
// // // //                 variant="h6"
// // // //                 fontWeight="bold"
// // // //                 gutterBottom
// // // //                 sx={{ color: "#4D3CC3", mb: 2 }}
// // // //               >
// // // //                 {isLoading ? (
// // // //                   <Skeleton width="70%" />
// // // //                 ) : (
// // // //                   "Key Performance Indicators"
// // // //                 )}
// // // //               </Typography>
// // // //               {isLoading ? (
// // // //                 <Stack spacing={3} sx={{ textAlign: "center", mt: 2 }}>
// // // //                   {[...Array(3)].map((_, i) => (
// // // //                     <Box key={i}>
// // // //                       <Skeleton
// // // //                         variant="text"
// // // //                         width="60%"
// // // //                         height={40}
// // // //                         sx={{ mx: "auto" }}
// // // //                       />
// // // //                       <Skeleton
// // // //                         variant="text"
// // // //                         width="80%"
// // // //                         height={20}
// // // //                         sx={{ mx: "auto" }}
// // // //                       />
// // // //                     </Box>
// // // //                   ))}
// // // //                 </Stack>
// // // //               ) : (
// // // //                 <Stack spacing={3} sx={{ textAlign: "center", mt: 1 }}>
// // // //                   {kpiConfig.map((kpi) => (
// // // //                     <Box key={kpi.key}>
// // // //                       <Typography
// // // //                         variant="h3"
// // // //                         fontWeight="bold"
// // // //                         sx={{
// // // //                           color: kpi.color,
// // // //                           fontSize: "2.5rem",
// // // //                           lineHeight: 1,
// // // //                           mb: 0.5,
// // // //                         }}
// // // //                       >
// // // //                         {Math.round(dashboardData.kpi_section[kpi.key] || 0)}
// // // //                         {kpi.suffix}
// // // //                       </Typography>
// // // //                       <Typography
// // // //                         variant="body2"
// // // //                         sx={{
// // // //                           color: "#666",
// // // //                           fontSize: "0.875rem",
// // // //                           fontWeight: "500",
// // // //                           lineHeight: 1.2,
// // // //                         }}
// // // //                       >
// // // //                         {kpi.label}
// // // //                       </Typography>
// // // //                     </Box>
// // // //                   ))}
// // // //                 </Stack>
// // // //               )}
// // // //             </Paper>
// // // //           </Grid>
// // // //         </Grid>
// // // //       </Box>
// // // //     </Layout>
// // // //   );
// // // // };

// // // // export default AdminDashboard;
// // // "use client";

// // // import { useState, useEffect } from "react";
// // // import {
// // //   Grid,
// // //   Typography,
// // //   Box,
// // //   Chip,
// // //   Table,
// // //   TableBody,
// // //   TableCell,
// // //   TableContainer,
// // //   TableHead,
// // //   TableRow,
// // //   Button,
// // //   Stack,
// // //   Skeleton,
// // //   Paper,
// // // } from "@mui/material";
// // // import { PeopleOutline, AssessmentOutlined } from "@mui/icons-material";
// // // import apiClient from "../context/apiClient";
// // // import Layout from "../components/common/Layout";

// // // const AdminDashboard = () => {
// // //   const navigate = (path) => {
// // //     console.log(`Navigate to: ${path}`);
// // //   };

// // //   const [isLoading, setIsLoading] = useState(true);

// // //   const [topCards, setTopCards] = useState([]);
// // //   const [dashboardData, setDashboardData] = useState({
// // //     regional_performance: [],
// // //     recent_system_activity: [],
// // //     kpi_section: {},
// // //   });

// // //   useEffect(() => {
// // //     const fetchData = async () => {
// // //       setIsLoading(true);
// // //       try {
// // //         const response = await apiClient.get("/dashboard/admin/");
// // //         const apiData = response.data;

// // //         const mappedKpiCards = apiData.kpi_cards
// // //           ? [
// // //               {
// // //                 label: "ACTIVE JOBS",
// // //                 value: apiData.kpi_cards.active_jobs || 0,
// // //               },
// // //               {
// // //                 label: "TOTAL APPLICATIONS",
// // //                 value: apiData.kpi_cards.total_applications || 0,
// // //               },
// // //               {
// // //                 label: "INTERVIEWS SCHEDULED",
// // //                 value: apiData.kpi_cards.interviews_scheduled || 0,
// // //               },
// // //               {
// // //                 label: "OFFERS EXTENDED",
// // //                 value: apiData.kpi_cards.offers_extended || 0,
// // //               },
// // //             ]
// // //           : [];
// // //         setTopCards(mappedKpiCards);

// // //         const mappedActivity = (apiData.recent_system_activity || []).map(
// // //           (activity) => ({
// // //             type: activity.type,
// // //             description: activity.description,
// // //             timestamp: activity.time_ago,
// // //           })
// // //         );

// // //         const mappedRegionalPerformance = (
// // //           apiData.regional_performance || []
// // //         ).map((region) => ({
// // //           ...region,
// // //           growth: region.growth,
// // //           placements: region.placements,
// // //         }));

// // //         const mappedKpiSection = {
// // //           ...(apiData.key_performance_indicators || {}),
// // //           employer_satisfaction_rate: 93.0,
// // //         };

// // //         setDashboardData({
// // //           recent_system_activity: mappedActivity,
// // //           regional_performance: mappedRegionalPerformance,
// // //           kpi_section: mappedKpiSection,
// // //         });
// // //       } catch (error) {
// // //         console.error("Failed to fetch dashboard data:", error);
// // //       } finally {
// // //         setIsLoading(false);
// // //       }
// // //     };

// // //     fetchData();
// // //   }, []);

// // //   const quickActions = [
// // //     {
// // //       label: "View Analytics",
// // //       link: "/gatep/admin/analytics",
// // //       icon: <AssessmentOutlined />,
// // //     },
// // //     {
// // //       label: "Manage Users",
// // //       link: "/gatep/admin/user",
// // //       icon: <PeopleOutline />,
// // //     },
// // //   ];

// // //   const kpiConfig = [
// // //     {
// // //       key: "profile_completion_rate",
// // //       label: "Profile Completion Rate",
// // //       suffix: "%",
// // //       color: "#42a5f5",
// // //     },
// // //     {
// // //       key: "interview_success_rate",
// // //       label: "Interview Success Rate",
// // //       suffix: "%",
// // //       color: "#66bb6a",
// // //     },
// // //     {
// // //       key: "average_placement_time_days",
// // //       label: "Average Placement Time",
// // //       suffix: " Days",
// // //       color: "#ffa726",
// // //     },
// // //   ];

// // //   const cardStyles = {
// // //     borderRadius: "12px",
// // //     boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
// // //     height: "100%",
// // //     backgroundColor: "#F1FFFA",
// // //     p: 2,
// // //   };

// // //   const primaryBlue = "#4C3CE2";

// // //   return (
// // //     <Layout>
// // //       <Box
// // //         sx={{
// // //           p: { xs: 2, md: 3 },
// // //           backgroundColor: "#f8f9fa",
// // //           minHeight: "100vh",
// // //         }}
// // //       >
// // //         {/* BIG CONTAINER FOR ALL PAGE CONTENT */}
// // //         <Stack direction="column" spacing={3}>
// // //           {/* ROW 1: QUICK ACTIONS */}
// // //           <Paper
// // //             sx={{
// // //               p: 3,
// // //               backgroundColor: "#F1FFFA",
// // //               borderRadius: "12px",
// // //               boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
// // //             }}
// // //           >
// // //             <Typography
// // //               variant="h4"
// // //               sx={{ fontWeight: "bold", color: "#4F46BA", mb: 2 }}
// // //             >
// // //               Quick Action
// // //             </Typography>
// // //             <Stack direction="row" spacing={2} alignItems="center">
// // //               {quickActions.map((action, index) => (
// // //                 <Button
// // //                   key={index}
// // //                   variant={index === 0 ? "contained" : "outlined"}
// // //                   startIcon={action.icon}
// // //                   onClick={() => navigate(action.link)}
// // //                   sx={{
// // //                     borderRadius: "12px",
// // //                     textTransform: "none",
// // //                     fontWeight: 500,
// // //                     boxShadow: "none",
// // //                     backgroundColor: index === 0 ? primaryBlue : "transparent",
// // //                     borderColor: primaryBlue,
// // //                     color: index === 0 ? "#FFFFFF" : primaryBlue,
// // //                     "&:hover": {
// // //                       backgroundColor:
// // //                         index === 0 ? "#4032bf" : "rgba(76, 58, 226, 0.04)",
// // //                       boxShadow: "none",
// // //                     },
// // //                   }}
// // //                 >
// // //                   {action.label}
// // //                 </Button>
// // //               ))}
// // //             </Stack>
// // //           </Paper>

// // //           {/* ROW 2: CARDS */}
// // //           <Box
// // //             sx={{
// // //               display: "flex",
// // //               flexDirection: "row",
// // //               gap: { xs: 1.5, sm: 2 },
// // //             }}
// // //           >
// // //             {isLoading
// // //               ? [...Array(4)].map((_, index) => (
// // //                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
// // //                     <Skeleton
// // //                       variant="rectangular"
// // //                       width="100%"
// // //                       height={140}
// // //                       sx={{ borderRadius: "12px" }}
// // //                     />
// // //                   </Box>
// // //                 ))
// // //               : topCards.map((card, index) => (
// // //                   // The key is flex: 1 (share space equally) and minWidth: 0 (allow shrinking)
// // //                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
// // //                     <Box
// // //                       sx={{
// // //                         width: "100%",
// // //                         height: 140,
// // //                         backgroundColor: "#4D3CC3",
// // //                         borderRadius: "12px",
// // //                         boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.15)",
// // //                         position: "relative",
// // //                         overflow: "hidden",
// // //                         p: { xs: 1.5, sm: 2, md: 2.5 },
// // //                         display: "flex",
// // //                         flexDirection: "column",
// // //                         justifyContent: "space-between",
// // //                       }}
// // //                     >
// // //                       <Box
// // //                         sx={{
// // //                           position: "absolute",
// // //                           top: 0,
// // //                           right: 0,
// // //                           width: "100%",
// // //                           height: "100%",
// // //                           zIndex: 0,
// // //                         }}
// // //                       >
// // //                         <svg
// // //                           width="100%"
// // //                           height="100%"
// // //                           viewBox="0 0 280 140"
// // //                           preserveAspectRatio="none"
// // //                           style={{ position: "absolute", right: 0, top: 0 }}
// // //                         >
// // //                           <path
// // //                             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
// // //                             fill="#F9B64C"
// // //                           />
// // //                         </svg>
// // //                       </Box>
// // //                       <Box sx={{ position: "relative", zIndex: 1 }}>
// // //                         <Typography
// // //                           sx={{
// // //                             fontSize: { xs: "0.65rem", sm: "0.75rem" },
// // //                             fontWeight: 600,
// // //                             color: "#B4B1D2",
// // //                             textTransform: "uppercase",
// // //                             lineHeight: 1.2,
// // //                             whiteSpace: "nowrap",
// // //                           }}
// // //                         >
// // //                           {card.label}
// // //                         </Typography>
// // //                       </Box>
// // //                       <Box sx={{ position: "relative", zIndex: 1 }}>
// // //                         <Typography
// // //                           sx={{
// // //                             fontSize: { xs: "1.75rem", md: "2rem" },
// // //                             fontWeight: "bold",
// // //                             color: "#FFFFFF",
// // //                             lineHeight: 1,
// // //                           }}
// // //                         >
// // //                           {card.value}
// // //                         </Typography>
// // //                       </Box>
// // //                     </Box>
// // //                   </Box>
// // //                 ))}
// // //           </Box>

// // //           {/* ROW 3: PANELS */}
// // //           <Grid container spacing={3}>
// // //             <Grid item xs={12} md={5}>
// // //               <Paper sx={cardStyles}>
// // //                 <Typography
// // //                   variant="h6"
// // //                   fontWeight="bold"
// // //                   gutterBottom
// // //                   sx={{ color: "#4D3CC3", mb: 2 }}
// // //                 >
// // //                   Regional Performance
// // //                 </Typography>
// // //                 <TableContainer>
// // //                   <Table size="small">
// // //                     <TableHead>
// // //                       <TableRow
// // //                         sx={{
// // //                           "& .MuiTableCell-root": {
// // //                             borderBottom: "1px solid #E8F5E8",
// // //                             color: "#666",
// // //                             fontWeight: "600",
// // //                             fontSize: "0.875rem",
// // //                             py: 1,
// // //                           },
// // //                         }}
// // //                       >
// // //                         <TableCell>Region</TableCell>
// // //                         <TableCell align="center">Placement</TableCell>
// // //                         <TableCell align="center">Growth</TableCell>
// // //                       </TableRow>
// // //                     </TableHead>
// // //                     <TableBody>
// // //                       {isLoading
// // //                         ? [...Array(6)].map((_, i) => (
// // //                             <TableRow key={i}>
// // //                               <TableCell colSpan={3}>
// // //                                 <Skeleton />
// // //                               </TableCell>
// // //                             </TableRow>
// // //                           ))
// // //                         : dashboardData.regional_performance.map(
// // //                             (region, i) => (
// // //                               <TableRow
// // //                                 key={i}
// // //                                 sx={{
// // //                                   "&:last-child td, &:last-child th": {
// // //                                     border: 0,
// // //                                   },
// // //                                   "&:hover": { backgroundColor: "#F8FFF8" },
// // //                                 }}
// // //                               >
// // //                                 <TableCell
// // //                                   sx={{
// // //                                     fontWeight: "500",
// // //                                     color: "#333",
// // //                                     py: 1.5,
// // //                                   }}
// // //                                 >
// // //                                   {region.region}
// // //                                 </TableCell>
// // //                                 <TableCell
// // //                                   align="center"
// // //                                   sx={{
// // //                                     fontWeight: "600",
// // //                                     color: "#2E7D32",
// // //                                     py: 1.5,
// // //                                   }}
// // //                                 >
// // //                                   {region.placements}
// // //                                 </TableCell>
// // //                                 <TableCell
// // //                                   align="center"
// // //                                   sx={{
// // //                                     fontWeight: "500",
// // //                                     color: "#4CAF50",
// // //                                     py: 1.5,
// // //                                   }}
// // //                                 >{`+${region.growth}%`}</TableCell>
// // //                               </TableRow>
// // //                             )
// // //                           )}
// // //                     </TableBody>
// // //                   </Table>
// // //                 </TableContainer>
// // //               </Paper>
// // //             </Grid>
// // //             <Grid item xs={12} md={4}>
// // //               <Paper sx={cardStyles}>
// // //                 <Typography
// // //                   variant="h6"
// // //                   fontWeight="bold"
// // //                   gutterBottom
// // //                   sx={{ color: "#4D3CC3", mb: 2 }}
// // //                 >
// // //                   Recent System Activity
// // //                 </Typography>
// // //                 <Stack spacing={2.5}>
// // //                   {isLoading
// // //                     ? [...Array(5)].map((_, i) => (
// // //                         <Skeleton key={i} height={50} />
// // //                       ))
// // //                     : dashboardData.recent_system_activity.map(
// // //                         (activity, i) => (
// // //                           <Box key={i} sx={{ position: "relative", pl: 2 }}>
// // //                             <Box
// // //                               sx={{
// // //                                 position: "absolute",
// // //                                 left: 0,
// // //                                 top: "50%",
// // //                                 transform: "translateY(-50%)",
// // //                                 width: "8px",
// // //                                 height: "8px",
// // //                                 backgroundColor: "#4CAF50",
// // //                                 borderRadius: "50%",
// // //                               }}
// // //                             />
// // //                             <Box
// // //                               display="flex"
// // //                               justifyContent="space-between"
// // //                               alignItems="flex-start"
// // //                               mb={0.5}
// // //                             >
// // //                               <Chip
// // //                                 label={activity.type}
// // //                                 size="small"
// // //                                 sx={{
// // //                                   backgroundColor: "#E8F5E8",
// // //                                   color: "#2E7D32",
// // //                                   borderRadius: "8px",
// // //                                   fontSize: "0.75rem",
// // //                                   fontWeight: "500",
// // //                                 }}
// // //                               />
// // //                               <Typography
// // //                                 variant="caption"
// // //                                 sx={{
// // //                                   color: "#FF5722",
// // //                                   fontSize: "0.75rem",
// // //                                   fontWeight: "500",
// // //                                 }}
// // //                               >
// // //                                 {activity.timestamp}
// // //                               </Typography>
// // //                             </Box>
// // //                             <Typography
// // //                               variant="body2"
// // //                               sx={{
// // //                                 fontWeight: "500",
// // //                                 color: "#333",
// // //                                 lineHeight: 1.4,
// // //                               }}
// // //                             >
// // //                               {activity.description}
// // //                             </Typography>
// // //                           </Box>
// // //                         )
// // //                       )}
// // //                 </Stack>
// // //               </Paper>
// // //             </Grid>
// // //             <Grid item xs={12} md={3}>
// // //               <Paper sx={cardStyles}>
// // //                 <Typography
// // //                   variant="h6"
// // //                   fontWeight="bold"
// // //                   gutterBottom
// // //                   sx={{ color: "#4D3CC3", mb: 2 }}
// // //                 >
// // //                   {isLoading ? (
// // //                     <Skeleton width="70%" />
// // //                   ) : (
// // //                     "Key Performance Indicators"
// // //                   )}
// // //                 </Typography>
// // //                 {isLoading ? (
// // //                   <Stack spacing={3} sx={{ textAlign: "center", mt: 2 }}>
// // //                     {[...Array(3)].map((_, i) => (
// // //                       <Box key={i}>
// // //                         <Skeleton
// // //                           variant="text"
// // //                           width="60%"
// // //                           height={40}
// // //                           sx={{ mx: "auto" }}
// // //                         />
// // //                         <Skeleton
// // //                           variant="text"
// // //                           width="80%"
// // //                           height={20}
// // //                           sx={{ mx: "auto" }}
// // //                         />
// // //                       </Box>
// // //                     ))}
// // //                   </Stack>
// // //                 ) : (
// // //                   <Stack spacing={3} sx={{ textAlign: "center", mt: 1 }}>
// // //                     {kpiConfig.map((kpi) => (
// // //                       <Box key={kpi.key}>
// // //                         <Typography
// // //                           variant="h3"
// // //                           fontWeight="bold"
// // //                           sx={{
// // //                             color: kpi.color,
// // //                             fontSize: "2.5rem",
// // //                             lineHeight: 1,
// // //                             mb: 0.5,
// // //                           }}
// // //                         >
// // //                           {Math.round(dashboardData.kpi_section[kpi.key] || 0)}
// // //                           {kpi.suffix}
// // //                         </Typography>
// // //                         <Typography
// // //                           variant="body2"
// // //                           sx={{
// // //                             color: "#666",
// // //                             fontSize: "0.875rem",
// // //                             fontWeight: "500",
// // //                             lineHeight: 1.2,
// // //                           }}
// // //                         >
// // //                           {kpi.label}
// // //                         </Typography>
// // //                       </Box>
// // //                     ))}
// // //                   </Stack>
// // //                 )}
// // //               </Paper>
// // //             </Grid>
// // //           </Grid>
// // //         </Stack>
// // //       </Box>
// // //     </Layout>
// // //   );
// // // };

// // // export default AdminDashboard;
// // "use client";

// // import { useState, useEffect } from "react";
// // import {
// //   Grid,
// //   Typography,
// //   Box,
// //   Chip,
// //   Table,
// //   TableBody,
// //   TableCell,
// //   TableContainer,
// //   TableHead,
// //   TableRow,
// //   Button,
// //   Stack,
// //   Skeleton,
// //   Paper,
// // } from "@mui/material";
// // import { PeopleOutline, AssessmentOutlined } from "@mui/icons-material";
// // import apiClient from "../context/apiClient";
// // import Layout from "../components/common/Layout";

// // const AdminDashboard = () => {
// //   const navigate = (path) => {
// //     console.log(`Navigate to: ${path}`);
// //   };

// //   const [isLoading, setIsLoading] = useState(true);

// //   const [topCards, setTopCards] = useState([]);
// //   const [dashboardData, setDashboardData] = useState({
// //     regional_performance: [],
// //     recent_system_activity: [],
// //     kpi_section: {},
// //   });

// //   useEffect(() => {
// //     const fetchData = async () => {
// //       setIsLoading(true);
// //       try {
// //         const response = await apiClient.get("/dashboard/admin/");
// //         const apiData = response.data;

// //         const mappedKpiCards = apiData.kpi_cards
// //           ? [
// //               {
// //                 label: "ACTIVE JOBS",
// //                 value: apiData.kpi_cards.active_jobs || 0,
// //               },
// //               {
// //                 label: "TOTAL APPLICATIONS",
// //                 value: apiData.kpi_cards.total_applications || 0,
// //               },
// //               {
// //                 label: "INTERVIEWS SCHEDULED",
// //                 value: apiData.kpi_cards.interviews_scheduled || 0,
// //               },
// //               {
// //                 label: "OFFERS EXTENDED",
// //                 value: apiData.kpi_cards.offers_extended || 0,
// //               },
// //             ]
// //           : [];
// //         setTopCards(mappedKpiCards);

// //         const mappedActivity = (apiData.recent_system_activity || []).map(
// //           (activity) => ({
// //             type: activity.type,
// //             description: activity.description,
// //             timestamp: activity.time_ago,
// //           })
// //         );

// //         const mappedRegionalPerformance = (
// //           apiData.regional_performance || []
// //         ).map((region) => ({
// //           ...region,
// //           growth: region.growth,
// //           placements: region.placements,
// //         }));

// //         const mappedKpiSection = {
// //           ...(apiData.key_performance_indicators || {}),
// //           employer_satisfaction_rate: 93.0,
// //         };

// //         setDashboardData({
// //           recent_system_activity: mappedActivity,
// //           regional_performance: mappedRegionalPerformance,
// //           kpi_section: mappedKpiSection,
// //         });
// //       } catch (error) {
// //         console.error("Failed to fetch dashboard data:", error);
// //       } finally {
// //         setIsLoading(false);
// //       }
// //     };

// //     fetchData();
// //   }, []);

// //   const quickActions = [
// //     {
// //       label: "View Analytics",
// //       link: "/gatep/admin/analytics",
// //       icon: <AssessmentOutlined />,
// //     },
// //     {
// //       label: "Manage Users",
// //       link: "/gatep/admin/user",
// //       icon: <PeopleOutline />,
// //     },
// //   ];

// //   const kpiConfig = [
// //     {
// //       key: "profile_completion_rate",
// //       label: "Profile Completion Rate",
// //       suffix: "%",
// //       color: "#42a5f5",
// //     },
// //     {
// //       key: "interview_success_rate",
// //       label: "Interview Success Rate",
// //       suffix: "%",
// //       color: "#66bb6a",
// //     },
// //     {
// //       key: "average_placement_time_days",
// //       label: "Average Placement Time",
// //       suffix: " Days",
// //       color: "#ffa726",
// //     },
// //   ];

// //   const cardStyles = {
// //     borderRadius: "12px",
// //     boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
// //     height: "100%",
// //     backgroundColor: "#F1FFFA",
// //     p: 2,
// //   };

// //   const primaryBlue = "#4C3CE2";

// //   return (
// //     <Layout>
// //       <Box
// //         sx={{
// //           p: { xs: 2, md: 3 },
// //           backgroundColor: "#f8f9fa",
// //           minHeight: "100vh",
// //         }}
// //       >
// //         {/* BIG CONTAINER FOR ALL PAGE CONTENT */}
// //         <Stack direction="column" spacing={3}>
// //           {/* ROW 1: QUICK ACTIONS */}
// //           <Paper
// //             sx={{
// //               p: 3,
// //               backgroundColor: "#F1FFFA",
// //               borderRadius: "12px",
// //               boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
// //             }}
// //           >
// //             <Typography
// //               variant="h4"
// //               sx={{ fontWeight: "bold", color: "#4F46BA", mb: 2 }}
// //             >
// //               Quick Action
// //             </Typography>
// //             <Stack direction="row" spacing={2} alignItems="center">
// //               {quickActions.map((action, index) => (
// //                 <Button
// //                   key={index}
// //                   variant={index === 0 ? "contained" : "outlined"}
// //                   startIcon={action.icon}
// //                   onClick={() => navigate(action.link)}
// //                   sx={{
// //                     borderRadius: "12px",
// //                     textTransform: "none",
// //                     fontWeight: 500,
// //                     boxShadow: "none",
// //                     backgroundColor: index === 0 ? primaryBlue : "transparent",
// //                     borderColor: primaryBlue,
// //                     color: index === 0 ? "#FFFFFF" : primaryBlue,
// //                     "&:hover": {
// //                       backgroundColor:
// //                         index === 0 ? "#4032bf" : "rgba(76, 58, 226, 0.04)",
// //                       boxShadow: "none",
// //                     },
// //                   }}
// //                 >
// //                   {action.label}
// //                 </Button>
// //               ))}
// //             </Stack>
// //           </Paper>

// //           {/* ROW 2: CARDS (2x2 on small screens, 1x4 on medium and up) */}
// //           <Grid container spacing={{ xs: 2, md: 3 }}>
// //             {isLoading
// //               ? [...Array(4)].map((_, index) => (
// //                   <Grid item xs={6} md={3} key={index}>
// //                     <Skeleton
// //                       variant="rectangular"
// //                       width="100%"
// //                       height={140}
// //                       sx={{ borderRadius: "12px" }}
// //                     />
// //                   </Grid>
// //                 ))
// //               : topCards.map((card, index) => (
// //                   <Grid item xs={6} md={3} key={index}>
// //                     <Box
// //                       sx={{
// //                         width: "100%",
// //                         height: 140,
// //                         backgroundColor: "#4D3CC3",
// //                         borderRadius: "12px",
// //                         boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.15)",
// //                         position: "relative",
// //                         overflow: "hidden",
// //                         p: { xs: 1.5, sm: 2, md: 2.5 },
// //                         display: "flex",
// //                         flexDirection: "column",
// //                         justifyContent: "space-between",
// //                       }}
// //                     >
// //                       <Box
// //                         sx={{
// //                           position: "absolute",
// //                           top: 0,
// //                           right: 0,
// //                           width: "100%",
// //                           height: "100%",
// //                           zIndex: 0,
// //                         }}
// //                       >
// //                         <svg
// //                           width="100%"
// //                           height="100%"
// //                           viewBox="0 0 280 140"
// //                           preserveAspectRatio="none"
// //                           style={{ position: "absolute", right: 0, top: 0 }}
// //                         >
// //                           <path
// //                             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
// //                             fill="#F9B64C"
// //                           />
// //                         </svg>
// //                       </Box>
// //                       <Box sx={{ position: "relative", zIndex: 1 }}>
// //                         <Typography
// //                           sx={{
// //                             fontSize: { xs: "0.65rem", sm: "0.75rem" },
// //                             fontWeight: 600,
// //                             color: "#B4B1D2",
// //                             textTransform: "uppercase",
// //                             lineHeight: 1.2,
// //                           }}
// //                         >
// //                           {card.label}
// //                         </Typography>
// //                       </Box>
// //                       <Box sx={{ position: "relative", zIndex: 1 }}>
// //                         <Typography
// //                           sx={{
// //                             fontSize: {
// //                               xs: "1.75rem",
// //                               sm: "2rem",
// //                               md: "2.25rem",
// //                             },
// //                             fontWeight: "bold",
// //                             color: "#FFFFFF",
// //                             lineHeight: 1,
// //                           }}
// //                         >
// //                           {card.value}
// //                         </Typography>
// //                       </Box>
// //                     </Box>
// //                   </Grid>
// //                 ))}
// //           </Grid>

// //           {/* ROW 3: PANELS (Stack on mobile, resize proportionally on larger screens) */}
// //           <Box
// //             sx={{
// //               display: "flex",
// //               gap: 3,
// //               flexDirection: { xs: "column", md: "row" }, // Stack on xs, row on md+
// //             }}
// //           >
// //             <Box sx={{ flex: { md: 5 }, minWidth: 0 }}>
// //               <Paper sx={cardStyles}>
// //                 <Typography
// //                   variant="h6"
// //                   fontWeight="bold"
// //                   gutterBottom
// //                   sx={{ color: "#4D3CC3", mb: 2 }}
// //                 >
// //                   Regional Performance
// //                 </Typography>
// //                 <TableContainer>
// //                   <Table size="small">
// //                     <TableHead>
// //                       <TableRow
// //                         sx={{
// //                           "& .MuiTableCell-root": {
// //                             borderBottom: "1px solid #E8F5E8",
// //                             color: "#666",
// //                             fontWeight: "600",
// //                             fontSize: "0.875rem",
// //                             py: 1,
// //                           },
// //                         }}
// //                       >
// //                         <TableCell>Region</TableCell>
// //                         <TableCell align="center">Placement</TableCell>
// //                         <TableCell align="center">Growth</TableCell>
// //                       </TableRow>
// //                     </TableHead>
// //                     <TableBody>
// //                       {isLoading
// //                         ? [...Array(6)].map((_, i) => (
// //                             <TableRow key={i}>
// //                               <TableCell colSpan={3}>
// //                                 <Skeleton />
// //                               </TableCell>
// //                             </TableRow>
// //                           ))
// //                         : dashboardData.regional_performance.map(
// //                             (region, i) => (
// //                               <TableRow
// //                                 key={i}
// //                                 sx={{
// //                                   "&:last-child td, &:last-child th": {
// //                                     border: 0,
// //                                   },
// //                                   "&:hover": { backgroundColor: "#F8FFF8" },
// //                                 }}
// //                               >
// //                                 <TableCell
// //                                   sx={{
// //                                     fontWeight: "500",
// //                                     color: "#333",
// //                                     py: 1.5,
// //                                   }}
// //                                 >
// //                                   {region.region}
// //                                 </TableCell>
// //                                 <TableCell
// //                                   align="center"
// //                                   sx={{
// //                                     fontWeight: "600",
// //                                     color: "#2E7D32",
// //                                     py: 1.5,
// //                                   }}
// //                                 >
// //                                   {region.placements}
// //                                 </TableCell>
// //                                 <TableCell
// //                                   align="center"
// //                                   sx={{
// //                                     fontWeight: "500",
// //                                     color: "#4CAF50",
// //                                     py: 1.5,
// //                                   }}
// //                                 >{`+${region.growth}%`}</TableCell>
// //                               </TableRow>
// //                             )
// //                           )}
// //                     </TableBody>
// //                   </Table>
// //                 </TableContainer>
// //               </Paper>
// //             </Box>
// //             <Box sx={{ flex: { md: 4 }, minWidth: 0 }}>
// //               <Paper sx={cardStyles}>
// //                 <Typography
// //                   variant="h6"
// //                   fontWeight="bold"
// //                   gutterBottom
// //                   sx={{ color: "#4D3CC3", mb: 2 }}
// //                 >
// //                   Recent System Activity
// //                 </Typography>
// //                 <Stack spacing={2.5}>
// //                   {isLoading
// //                     ? [...Array(5)].map((_, i) => (
// //                         <Skeleton key={i} height={50} />
// //                       ))
// //                     : dashboardData.recent_system_activity.map(
// //                         (activity, i) => (
// //                           <Box key={i} sx={{ position: "relative", pl: 2 }}>
// //                             <Box
// //                               sx={{
// //                                 position: "absolute",
// //                                 left: 0,
// //                                 top: "50%",
// //                                 transform: "translateY(-50%)",
// //                                 width: "8px",
// //                                 height: "8px",
// //                                 backgroundColor: "#4CAF50",
// //                                 borderRadius: "50%",
// //                               }}
// //                             />
// //                             <Box
// //                               display="flex"
// //                               justifyContent="space-between"
// //                               alignItems="flex-start"
// //                               mb={0.5}
// //                             >
// //                               <Chip
// //                                 label={activity.type}
// //                                 size="small"
// //                                 sx={{
// //                                   backgroundColor: "#E8F5E8",
// //                                   color: "#2E7D32",
// //                                   borderRadius: "8px",
// //                                   fontSize: "0.75rem",
// //                                   fontWeight: "500",
// //                                 }}
// //                               />
// //                               <Typography
// //                                 variant="caption"
// //                                 sx={{
// //                                   color: "#FF5722",
// //                                   fontSize: "0.75rem",
// //                                   fontWeight: "500",
// //                                 }}
// //                               >
// //                                 {activity.timestamp}
// //                               </Typography>
// //                             </Box>
// //                             <Typography
// //                               variant="body2"
// //                               sx={{
// //                                 fontWeight: "500",
// //                                 color: "#333",
// //                                 lineHeight: 1.4,
// //                               }}
// //                             >
// //                               {activity.description}
// //                             </Typography>
// //                           </Box>
// //                         )
// //                       )}
// //                 </Stack>
// //               </Paper>
// //             </Box>
// //             <Box sx={{ flex: { md: 3 }, minWidth: 0 }}>
// //               <Paper sx={cardStyles}>
// //                 <Typography
// //                   variant="h6"
// //                   fontWeight="bold"
// //                   gutterBottom
// //                   sx={{ color: "#4D3CC3", mb: 2 }}
// //                 >
// //                   {isLoading ? (
// //                     <Skeleton width="70%" />
// //                   ) : (
// //                     "Key Performance Indicators"
// //                   )}
// //                 </Typography>
// //                 {isLoading ? (
// //                   <Stack spacing={3} sx={{ textAlign: "center", mt: 2 }}>
// //                     {[...Array(3)].map((_, i) => (
// //                       <Box key={i}>
// //                         <Skeleton
// //                           variant="text"
// //                           width="60%"
// //                           height={40}
// //                           sx={{ mx: "auto" }}
// //                         />
// //                         <Skeleton
// //                           variant="text"
// //                           width="80%"
// //                           height={20}
// //                           sx={{ mx: "auto" }}
// //                         />
// //                       </Box>
// //                     ))}
// //                   </Stack>
// //                 ) : (
// //                   <Stack spacing={3} sx={{ textAlign: "center", mt: 1 }}>
// //                     {kpiConfig.map((kpi) => (
// //                       <Box key={kpi.key}>
// //                         <Typography
// //                           variant="h3"
// //                           fontWeight="bold"
// //                           sx={{
// //                             color: kpi.color,
// //                             fontSize: "2.5rem",
// //                             lineHeight: 1,
// //                             mb: 0.5,
// //                           }}
// //                         >
// //                           {Math.round(dashboardData.kpi_section[kpi.key] || 0)}
// //                           {kpi.suffix}
// //                         </Typography>
// //                         <Typography
// //                           variant="body2"
// //                           sx={{
// //                             color: "#666",
// //                             fontSize: "0.875rem",
// //                             fontWeight: "500",
// //                             lineHeight: 1.2,
// //                           }}
// //                         >
// //                           {kpi.label}
// //                         </Typography>
// //                       </Box>
// //                     ))}
// //                   </Stack>
// //                 )}
// //               </Paper>
// //             </Box>
// //           </Box>
// //         </Stack>
// //       </Box>
// //     </Layout>
// //   );
// // };

// // export default AdminDashboard;
// "use client";

// import { useState, useEffect } from "react";
// import {
//   Grid,
//   Typography,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Button,
//   Stack,
//   Skeleton,
//   Paper,
// } from "@mui/material";
// import { PeopleOutline, AssessmentOutlined } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";

// const AdminDashboard = () => {
//   const navigate = (path) => {
//     console.log(`Navigate to: ${path}`);
//   };

//   const [isLoading, setIsLoading] = useState(true);

//   const [topCards, setTopCards] = useState([]);
//   const [dashboardData, setDashboardData] = useState({
//     regional_performance: [],
//     recent_system_activity: [],
//     kpi_section: {},
//   });

//   useEffect(() => {
//     const fetchData = async () => {
//       setIsLoading(true);
//       try {
//         const response = await apiClient.get("/dashboard/admin/");
//         const apiData = response.data;

//         const mappedKpiCards = apiData.kpi_cards
//           ? [
//               {
//                 label: "ACTIVE JOBS",
//                 value: apiData.kpi_cards.active_jobs || 0,
//               },
//               {
//                 label: "TOTAL APPLICATIONS",
//                 value: apiData.kpi_cards.total_applications || 0,
//               },
//               {
//                 label: "INTERVIEWS SCHEDULED",
//                 value: apiData.kpi_cards.interviews_scheduled || 0,
//               },
//               {
//                 label: "OFFERS EXTENDED",
//                 value: apiData.kpi_cards.offers_extended || 0,
//               },
//             ]
//           : [];
//         setTopCards(mappedKpiCards);

//         const mappedActivity = (apiData.recent_system_activity || []).map(
//           (activity) => ({
//             type: activity.type,
//             description: activity.description,
//             timestamp: activity.time_ago,
//           })
//         );

//         const mappedRegionalPerformance = (
//           apiData.regional_performance || []
//         ).map((region) => ({
//           ...region,
//           growth: region.growth,
//           placements: region.placements,
//         }));

//         const mappedKpiSection = {
//           ...(apiData.key_performance_indicators || {}),
//           employer_satisfaction_rate: 93.0,
//         };

//         setDashboardData({
//           recent_system_activity: mappedActivity,
//           regional_performance: mappedRegionalPerformance,
//           kpi_section: mappedKpiSection,
//         });
//       } catch (error) {
//         console.error("Failed to fetch dashboard data:", error);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   const quickActions = [
//     {
//       label: "View Analytics",
//       link: "/gatep/admin/analytics",
//       icon: <AssessmentOutlined />,
//     },
//     {
//       label: "Manage Users",
//       link: "/gatep/admin/user",
//       icon: <PeopleOutline />,
//     },
//   ];

//   const kpiConfig = [
//     {
//       key: "profile_completion_rate",
//       label: "Profile Completion Rate",
//       suffix: "%",
//       color: "#42a5f5",
//     },
//     {
//       key: "interview_success_rate",
//       label: "Interview Success Rate",
//       suffix: "%",
//       color: "#66bb6a",
//     },
//     {
//       key: "average_placement_time_days",
//       label: "Average Placement Time",
//       suffix: " Days",
//       color: "#ffa726",
//     },
//   ];

//   const cardStyles = {
//     borderRadius: "12px",
//     boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
//     height: "100%",
//     backgroundColor: "#F1FFFA",
//     p: 2,
//   };

//   const primaryBlue = "#4C3CE2";

//   return (
//     <Layout>
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           backgroundColor: "#f8f9fa",
//           minHeight: "100vh",
//         }}
//       >
//         <Stack direction="column" spacing={3}>
//           {/* ROW 1: QUICK ACTIONS */}
//           <Paper
//             sx={{
//               p: 3,
//               backgroundColor: "#F1FFFA",
//               borderRadius: "12px",
//               boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
//             }}
//           >
//             <Typography
//               variant="h4"
//               sx={{ fontWeight: "bold", color: "#4F46BA", mb: 2 }}
//             >
//               Quick Action
//             </Typography>
//             <Stack direction="row" spacing={2} alignItems="center">
//               {quickActions.map((action, index) => (
//                 <Button
//                   key={index}
//                   variant={index === 0 ? "contained" : "outlined"}
//                   startIcon={action.icon}
//                   onClick={() => navigate(action.link)}
//                   sx={{
//                     borderRadius: "12px",
//                     textTransform: "none",
//                     fontWeight: 500,
//                     boxShadow: "none",
//                     backgroundColor: index === 0 ? primaryBlue : "transparent",
//                     borderColor: primaryBlue,
//                     color: index === 0 ? "#FFFFFF" : primaryBlue,
//                     "&:hover": {
//                       backgroundColor:
//                         index === 0 ? "#4032bf" : "rgba(76, 58, 226, 0.04)",
//                       boxShadow: "none",
//                     },
//                   }}
//                 >
//                   {action.label}
//                 </Button>
//               ))}
//             </Stack>
//           </Paper>

//           {/* ROW 2: CARDS (Always in one row, fluidly resizing) */}
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "row",
//               gap: { xs: 1.5, sm: 2, md: 3 },
//             }}
//           >
//             {isLoading
//               ? [...Array(4)].map((_, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Skeleton
//                       variant="rectangular"
//                       width="100%"
//                       height={140}
//                       sx={{ borderRadius: "12px" }}
//                     />
//                   </Box>
//                 ))
//               : topCards.map((card, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Box
//                       sx={{
//                         width: "100%",
//                         height: 140,
//                         backgroundColor: "#4D3CC3",
//                         borderRadius: "12px",
//                         boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.15)",
//                         position: "relative",
//                         overflow: "hidden",
//                         p: { xs: 1.5, sm: 2, md: 2.5 },
//                         display: "flex",
//                         flexDirection: "column",
//                         justifyContent: "space-between",
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           position: "absolute",
//                           top: 0,
//                           right: 0,
//                           width: "100%",
//                           height: "100%",
//                           zIndex: 0,
//                         }}
//                       >
//                         <svg
//                           width="100%"
//                           height="100%"
//                           viewBox="0 0 280 140"
//                           preserveAspectRatio="none"
//                           style={{ position: "absolute", right: 0, top: 0 }}
//                         >
//                           <path
//                             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
//                             fill="#F9B64C"
//                           />
//                         </svg>
//                       </Box>
//                       <Box
//                         sx={{
//                           position: "relative",
//                           zIndex: 1,
//                           overflow: "hidden",
//                         }}
//                       >
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "0.65rem", sm: "0.75rem" },
//                             fontWeight: 600,
//                             color: "#B4B1D2",
//                             textTransform: "uppercase",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {card.label}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ position: "relative", zIndex: 1 }}>
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "1.75rem", md: "2.25rem" },
//                             fontWeight: "bold",
//                             color: "#FFFFFF",
//                             lineHeight: 1,
//                           }}
//                         >
//                           {card.value}
//                         </Typography>
//                       </Box>
//                     </Box>
//                   </Box>
//                 ))}
//           </Box>

//           {/* ROW 3: PANELS (Stack on mobile, resize proportionally on larger screens) */}
//           <Box
//             sx={{
//               display: "flex",
//               gap: 3,
//               flexDirection: { xs: "column", md: "row" },
//             }}
//           >
//             <Box sx={{ flex: { md: 5 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: "#4D3CC3", mb: 2 }}
//                 >
//                   Regional Performance
//                 </Typography>
//                 <TableContainer>
//                   <Table size="small">
//                     <TableHead>
//                       <TableRow
//                         sx={{
//                           "& .MuiTableCell-root": {
//                             borderBottom: "1px solid #E8F5E8",
//                             color: "#666",
//                             fontWeight: "600",
//                             fontSize: "0.875rem",
//                             py: 1,
//                           },
//                         }}
//                       >
//                         <TableCell>Region</TableCell>
//                         <TableCell align="center">Placement</TableCell>
//                         <TableCell align="center">Growth</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {isLoading
//                         ? [...Array(6)].map((_, i) => (
//                             <TableRow key={i}>
//                               <TableCell colSpan={3}>
//                                 <Skeleton />
//                               </TableCell>
//                             </TableRow>
//                           ))
//                         : dashboardData.regional_performance.map(
//                             (region, i) => (
//                               <TableRow
//                                 key={i}
//                                 sx={{
//                                   "&:last-child td, &:last-child th": {
//                                     border: 0,
//                                   },
//                                   "&:hover": { backgroundColor: "#F8FFF8" },
//                                 }}
//                               >
//                                 <TableCell
//                                   sx={{
//                                     fontWeight: "500",
//                                     color: "#333",
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {region.region}
//                                 </TableCell>
//                                 <TableCell
//                                   align="center"
//                                   sx={{
//                                     fontWeight: "600",
//                                     color: "#2E7D32",
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {region.placements}
//                                 </TableCell>
//                                 <TableCell
//                                   align="center"
//                                   sx={{
//                                     fontWeight: "500",
//                                     color: "#4CAF50",
//                                     py: 1.5,
//                                   }}
//                                 >{`+${region.growth}%`}</TableCell>
//                               </TableRow>
//                             )
//                           )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 4 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: "#4D3CC3", mb: 2 }}
//                 >
//                   Recent System Activity
//                 </Typography>
//                 <Stack spacing={2.5}>
//                   {isLoading
//                     ? [...Array(5)].map((_, i) => (
//                         <Skeleton key={i} height={50} />
//                       ))
//                     : dashboardData.recent_system_activity.map(
//                         (activity, i) => (
//                           <Box key={i} sx={{ position: "relative", pl: 2 }}>
//                             <Box
//                               sx={{
//                                 position: "absolute",
//                                 left: 0,
//                                 top: "50%",
//                                 transform: "translateY(-50%)",
//                                 width: "8px",
//                                 height: "8px",
//                                 backgroundColor: "#4CAF50",
//                                 borderRadius: "50%",
//                               }}
//                             />
//                             <Box
//                               display="flex"
//                               justifyContent="space-between"
//                               alignItems="flex-start"
//                               mb={0.5}
//                             >
//                               <Chip
//                                 label={activity.type}
//                                 size="small"
//                                 sx={{
//                                   backgroundColor: "#E8F5E8",
//                                   color: "#2E7D32",
//                                   borderRadius: "8px",
//                                   fontSize: "0.75rem",
//                                   fontWeight: "500",
//                                 }}
//                               />
//                               <Typography
//                                 variant="caption"
//                                 sx={{
//                                   color: "#FF5722",
//                                   fontSize: "0.75rem",
//                                   fontWeight: "500",
//                                 }}
//                               >
//                                 {activity.timestamp}
//                               </Typography>
//                             </Box>
//                             <Typography
//                               variant="body2"
//                               sx={{
//                                 fontWeight: "500",
//                                 color: "#333",
//                                 lineHeight: 1.4,
//                               }}
//                             >
//                               {activity.description}
//                             </Typography>
//                           </Box>
//                         )
//                       )}
//                 </Stack>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 3 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: "#4D3CC3", mb: 2 }}
//                 >
//                   {isLoading ? (
//                     <Skeleton width="70%" />
//                   ) : (
//                     "Key Performance Indicators"
//                   )}
//                 </Typography>
//                 {isLoading ? (
//                   <Stack spacing={3} sx={{ textAlign: "center", mt: 2 }}>
//                     {[...Array(3)].map((_, i) => (
//                       <Box key={i}>
//                         <Skeleton
//                           variant="text"
//                           width="60%"
//                           height={40}
//                           sx={{ mx: "auto" }}
//                         />
//                         <Skeleton
//                           variant="text"
//                           width="80%"
//                           height={20}
//                           sx={{ mx: "auto" }}
//                         />
//                       </Box>
//                     ))}
//                   </Stack>
//                 ) : (
//                   <Stack spacing={3} sx={{ textAlign: "center", mt: 1 }}>
//                     {kpiConfig.map((kpi) => (
//                       <Box key={kpi.key}>
//                         <Typography
//                           variant="h3"
//                           fontWeight="bold"
//                           sx={{
//                             color: kpi.color,
//                             fontSize: "2.5rem",
//                             lineHeight: 1,
//                             mb: 0.5,
//                           }}
//                         >
//                           {Math.round(dashboardData.kpi_section[kpi.key] || 0)}
//                           {kpi.suffix}
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           sx={{
//                             color: "#666",
//                             fontSize: "0.875rem",
//                             fontWeight: "500",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {kpi.label}
//                         </Typography>
//                       </Box>
//                     ))}
//                   </Stack>
//                 )}
//               </Paper>
//             </Box>
//           </Box>
//         </Stack>
//       </Box>
//     </Layout>
//   );
// };

// export default AdminDashboard;
// "use client";

// import { useState, useEffect } from "react";
// import {
//   Grid,
//   Typography,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Button,
//   Stack,
//   Skeleton,
//   Paper,
// } from "@mui/material";
// import { useTheme, alpha } from "@mui/material/styles"; // Import useTheme and alpha
// import { PeopleOutline, AssessmentOutlined } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";

// const AdminDashboard = () => {
//   const theme = useTheme(); // Hook into the theme
//   const navigate = (path) => {
//     console.log(`Navigate to: ${path}`);
//   };

//   const [isLoading, setIsLoading] = useState(true);

//   const [topCards, setTopCards] = useState([]);
//   const [dashboardData, setDashboardData] = useState({
//     regional_performance: [],
//     recent_system_activity: [],
//     kpi_section: {},
//   });

//   useEffect(() => {
//     const fetchData = async () => {
//       setIsLoading(true);
//       try {
//         const response = await apiClient.get("/dashboard/admin/");
//         const apiData = response.data;

//         const mappedKpiCards = apiData.kpi_cards
//           ? [
//               {
//                 label: "ACTIVE JOBS",
//                 value: apiData.kpi_cards.active_jobs || 0,
//               },
//               {
//                 label: "TOTAL APPLICATIONS",
//                 value: apiData.kpi_cards.total_applications || 0,
//               },
//               {
//                 label: "INTERVIEWS SCHEDULED",
//                 value: apiData.kpi_cards.interviews_scheduled || 0,
//               },
//               {
//                 label: "OFFERS EXTENDED",
//                 value: apiData.kpi_cards.offers_extended || 0,
//               },
//             ]
//           : [];
//         setTopCards(mappedKpiCards);

//         const mappedActivity = (apiData.recent_system_activity || []).map(
//           (activity) => ({
//             type: activity.type,
//             description: activity.description,
//             timestamp: activity.time_ago,
//           })
//         );

//         const mappedRegionalPerformance = (
//           apiData.regional_performance || []
//         ).map((region) => ({
//           ...region,
//           growth: region.growth,
//           placements: region.placements,
//         }));

//         const mappedKpiSection = {
//           ...(apiData.key_performance_indicators || {}),
//           employer_satisfaction_rate: 93.0,
//         };

//         setDashboardData({
//           recent_system_activity: mappedActivity,
//           regional_performance: mappedRegionalPerformance,
//           kpi_section: mappedKpiSection,
//         });
//       } catch (error) {
//         console.error("Failed to fetch dashboard data:", error);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   const quickActions = [
//     {
//       label: "View Analytics",
//       link: "/gatep/admin/analytics",
//       icon: <AssessmentOutlined />,
//     },
//     {
//       label: "Manage Users",
//       link: "/gatep/admin/user",
//       icon: <PeopleOutline />,
//     },
//   ];

//   // Defined inside the component to get access to the theme object
//   const kpiConfig = [
//     {
//       key: "profile_completion_rate",
//       label: "Profile Completion Rate",
//       suffix: "%",
//       color: theme.palette.info.main,
//     },
//     {
//       key: "interview_success_rate",
//       label: "Interview Success Rate",
//       suffix: "%",
//       color: theme.palette.success.main,
//     },
//     {
//       key: "average_placement_time_days",
//       label: "Average Placement Time",
//       suffix: " Days",
//       color: theme.palette.warning.main,
//     },
//   ];

//   const cardStyles = {
//     borderRadius: "12px",
//     boxShadow: theme.shadows[1], // Using theme shadow
//     height: "100%",
//     backgroundColor: theme.palette.mint.main, // Using theme color
//     p: 2,
//   };

//   return (
//     <Layout title="Admin Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           backgroundColor: theme.palette.background.default, // Using theme color
//           minHeight: "100vh",
//         }}
//       >
//         <Stack direction="column" spacing={3}>
//           {/* ROW 1: QUICK ACTIONS */}
//           <Paper
//             sx={{
//               p: 3,
//               backgroundColor: theme.palette.mint.main, // Using theme color
//               borderRadius: "12px",
//               boxShadow: theme.shadows[1], // Using theme shadow
//             }}
//           >
//             <Typography
//               variant="h4"
//               sx={{
//                 fontWeight: "bold",
//                 color: theme.palette.primary.main,
//                 mb: 2,
//               }} // Using theme color
//             >
//               Quick Action
//             </Typography>
//             <Stack direction="row" spacing={2} alignItems="center">
//               {quickActions.map((action, index) => (
//                 <Button
//                   key={index}
//                   variant={index === 0 ? "contained" : "outlined"}
//                   color="primary" // Let the theme handle the colors
//                   startIcon={action.icon}
//                   onClick={() => navigate(action.link)}
//                   sx={{
//                     borderRadius: "12px",
//                     textTransform: "none",
//                     fontWeight: 500,
//                   }}
//                 >
//                   {action.label}
//                 </Button>
//               ))}
//             </Stack>
//           </Paper>

//           {/* ROW 2: CARDS (Always in one row, fluidly resizing) */}
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "row",
//               gap: { xs: 1.5, sm: 2, md: 3 },
//             }}
//           >
//             {isLoading
//               ? [...Array(4)].map((_, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Skeleton
//                       variant="rectangular"
//                       width="100%"
//                       height={140}
//                       sx={{ borderRadius: "12px" }}
//                     />
//                   </Box>
//                 ))
//               : topCards.map((card, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Box
//                       sx={{
//                         width: "100%",
//                         height: 140,
//                         backgroundColor: theme.palette.primary.main, // Using theme color
//                         borderRadius: "12px",
//                         boxShadow: theme.shadows[2], // Using theme shadow
//                         position: "relative",
//                         overflow: "hidden",
//                         p: { xs: 1.5, sm: 2, md: 2.5 },
//                         display: "flex",
//                         flexDirection: "column",
//                         justifyContent: "space-between",
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           position: "absolute",
//                           top: 0,
//                           right: 0,
//                           width: "100%",
//                           height: "100%",
//                           zIndex: 0,
//                         }}
//                       >
//                         <svg
//                           width="100%"
//                           height="100%"
//                           viewBox="0 0 280 140"
//                           preserveAspectRatio="none"
//                           style={{ position: "absolute", right: 0, top: 0 }}
//                         >
//                           <path
//                             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
//                             fill={theme.palette.accent.main} // Using theme accent color
//                           />
//                         </svg>
//                       </Box>
//                       <Box
//                         sx={{
//                           position: "relative",
//                           zIndex: 1,
//                         }}
//                       >
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "0.65rem", sm: "0.75rem" },
//                             fontWeight: 600,
//                             color: alpha(
//                               theme.palette.primary.contrastText,
//                               0.8
//                             ), // Using theme color
//                             textTransform: "uppercase",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {card.label}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ position: "relative", zIndex: 1 }}>
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "1.75rem", md: "2.25rem" },
//                             fontWeight: "bold",
//                             color: theme.palette.primary.contrastText, // Using theme color
//                             lineHeight: 1,
//                           }}
//                         >
//                           {card.value}
//                         </Typography>
//                       </Box>
//                     </Box>
//                   </Box>
//                 ))}
//           </Box>

//           {/* ROW 3: PANELS (Stack on mobile, resize proportionally on larger screens) */}
//           <Box
//             sx={{
//               display: "flex",
//               gap: 3,
//               flexDirection: { xs: "column", md: "row" },
//             }}
//           >
//             <Box sx={{ flex: { md: 5 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: theme.palette.primary.main, mb: 2 }} // Using theme color
//                 >
//                   Regional Performance
//                 </Typography>
//                 <TableContainer>
//                   <Table size="small">
//                     <TableHead>
//                       <TableRow
//                         sx={{
//                           "& .MuiTableCell-root": {
//                             borderBottom: `1px solid ${theme.palette.divider}`, // Using theme color
//                             color: theme.palette.text.secondary, // Using theme color
//                             fontWeight: "600",
//                             fontSize: "0.875rem",
//                             py: 1,
//                           },
//                         }}
//                       >
//                         <TableCell>Region</TableCell>
//                         <TableCell align="center">Placement</TableCell>
//                         <TableCell align="center">Growth</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {isLoading
//                         ? [...Array(6)].map((_, i) => (
//                             <TableRow key={i}>
//                               <TableCell colSpan={3}>
//                                 {" "}
//                                 <Skeleton />{" "}
//                               </TableCell>
//                             </TableRow>
//                           ))
//                         : dashboardData.regional_performance.map(
//                             (region, i) => (
//                               <TableRow
//                                 key={i}
//                                 sx={{
//                                   "&:last-child td, &:last-child th": {
//                                     border: 0,
//                                   },
//                                   "&:hover": {
//                                     backgroundColor: theme.palette.action.hover,
//                                   },
//                                 }}
//                               >
//                                 <TableCell
//                                   sx={{
//                                     fontWeight: "500",
//                                     color: theme.palette.text.primary,
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {region.region}
//                                 </TableCell>
//                                 <TableCell
//                                   align="center"
//                                   sx={{
//                                     fontWeight: "600",
//                                     color: theme.palette.secondary.main,
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {region.placements}
//                                 </TableCell>
//                                 <TableCell
//                                   align="center"
//                                   sx={{
//                                     fontWeight: "500",
//                                     color: theme.palette.secondary.main,
//                                     py: 1.5,
//                                   }}
//                                 >
//                                   {`+${region.growth}%`}
//                                 </TableCell>
//                               </TableRow>
//                             )
//                           )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 4 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: theme.palette.primary.main, mb: 2 }} // Using theme color
//                 >
//                   Recent System Activity
//                 </Typography>
//                 <Stack spacing={2.5}>
//                   {isLoading
//                     ? [...Array(5)].map((_, i) => (
//                         <Skeleton key={i} height={50} />
//                       ))
//                     : dashboardData.recent_system_activity.map(
//                         (activity, i) => (
//                           <Box key={i} sx={{ position: "relative", pl: 2 }}>
//                             <Box
//                               sx={{
//                                 position: "absolute",
//                                 left: 0,
//                                 top: "50%",
//                                 transform: "translateY(-50%)",
//                                 width: "8px",
//                                 height: "8px",
//                                 backgroundColor: theme.palette.success.main,
//                                 borderRadius: "50%",
//                               }}
//                             />
//                             <Box
//                               display="flex"
//                               justifyContent="space-between"
//                               alignItems="flex-start"
//                               mb={0.5}
//                             >
//                               <Chip
//                                 label={activity.type}
//                                 size="small"
//                                 sx={{
//                                   backgroundColor: alpha(
//                                     theme.palette.secondary.main,
//                                     0.1
//                                   ),
//                                   color: theme.palette.secondary.main,
//                                   borderRadius: "8px",
//                                   fontSize: "0.75rem",
//                                   fontWeight: "500",
//                                 }}
//                               />
//                               <Typography
//                                 variant="caption"
//                                 sx={{
//                                   color: theme.palette.error.main,
//                                   fontSize: "0.75rem",
//                                   fontWeight: "500",
//                                 }}
//                               >
//                                 {activity.timestamp}
//                               </Typography>
//                             </Box>
//                             <Typography
//                               variant="body2"
//                               sx={{
//                                 fontWeight: "500",
//                                 color: theme.palette.text.primary,
//                                 lineHeight: 1.4,
//                               }}
//                             >
//                               {activity.description}
//                             </Typography>
//                           </Box>
//                         )
//                       )}
//                 </Stack>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 3 }, minWidth: 0 }}>
//               <Paper sx={cardStyles}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   gutterBottom
//                   sx={{ color: theme.palette.primary.main, mb: 2 }} // Using theme color
//                 >
//                   {isLoading ? (
//                     <Skeleton width="70%" />
//                   ) : (
//                     "Key Performance Indicators"
//                   )}
//                 </Typography>
//                 {isLoading ? (
//                   <Stack spacing={3} sx={{ textAlign: "center", mt: 2 }}>
//                     {[...Array(3)].map((_, i) => (
//                       <Box key={i}>
//                         <Skeleton
//                           variant="text"
//                           width="60%"
//                           height={40}
//                           sx={{ mx: "auto" }}
//                         />
//                         <Skeleton
//                           variant="text"
//                           width="80%"
//                           height={20}
//                           sx={{ mx: "auto" }}
//                         />
//                       </Box>
//                     ))}
//                   </Stack>
//                 ) : (
//                   <Stack spacing={3} sx={{ textAlign: "center", mt: 1 }}>
//                     {kpiConfig.map((kpi) => (
//                       <Box key={kpi.key}>
//                         <Typography
//                           variant="h3"
//                           fontWeight="bold"
//                           sx={{
//                             color: kpi.color, // Using theme color from the config object
//                             fontSize: "2.5rem",
//                             lineHeight: 1,
//                             mb: 0.5,
//                           }}
//                         >
//                           {Math.round(dashboardData.kpi_section[kpi.key] || 0)}
//                           {kpi.suffix}
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           sx={{
//                             color: theme.palette.text.secondary,
//                             fontSize: "0.875rem",
//                             fontWeight: "500",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {kpi.label}
//                         </Typography>
//                       </Box>
//                     ))}
//                   </Stack>
//                 )}
//               </Paper>
//             </Box>
//           </Box>
//         </Stack>
//       </Box>
//     </Layout>
//   );
// };

// export default AdminDashboard;
